import './taskpane.css';
import { AuthService } from '../auth/AuthService';
import type { LicenseCheck } from '../auth/AuthService';
import { TokenManager } from '../auth/TokenManager';
import type { UserInfo } from '../auth/TokenManager';
import { ViewManager } from '../ui/ViewManager';
import { AccessibilityManager } from '../utils/AccessibilityManager';
import { PerformanceManager } from '../utils/PerformanceManager';
import { ApiService } from '../utils/ApiService';
import { CompanySwitcher } from '../ui/CompanySwitcher';
import { SIGN_IN_FIRST_MESSAGE, WorkbookCompanyPanel, finishMoveMessage, owedConversionRefusal } from '../ui/workbookCompanyPanel';
import { mayRecalculateFor, moveWorkbookTo, officeReady } from '../services/workbookCompany';
import {
  calculateSheets,
  everyCellSetOff,
  manualCalculationOf,
  manualConversionRefusal,
  moveFailed,
  moveStarted,
  recalculateThisWorkbook,
  waitForCells,
  withManualHint,
} from '../services/workbookRecalc';
import { allCellsSetOffSince, duringPaneAction, sheetCellsSetOff } from '../services/paneActivity';
import { resetSessionCard } from '../ui/sessionCard';
import { arrowRightIcon, chevronRightIcon } from '../ui/icons';
import { DeviceFingerprint } from '../utils/DeviceFingerprint';
import { debugLog } from '../utils/debug';
import { CellError } from '../constants/ErrorMessages';
import { FormulaBuilder, getFunctionName, functionTypeLabel } from './FormulaBuilder';
import { HardcodeService, noFigureMessage, noFigurePreview } from './HardcodeService';
import { parseBifrostFormula } from './formulaParsing';
import { ADDIN_NAMESPACE, RIBBON_ID_SUFFIX, HELP_URL, PORTAL_URL } from '../config/addinNamespace';
import { formulaReferenceUrl, openFormulaReference } from './formulaReference';
import type { HardcodeScope } from './HardcodeService';
import { JournalApiError, JournalService, awaitsApprovalInErp } from '../services/JournalService';
import { JournalSendFlow } from '../services/JournalSendFlow';
import { JournalEditWatcher } from '../services/JournalEditWatcher';
import { ownerScope } from '../services/JournalListService';
import { JournalPaneGuard } from '../services/JournalPaneGuard';
import { JournalActionRunner } from '../services/JournalActionRunner';
import { readJournalTabData, type JournalTabData } from '../services/journalTabData';
import { TwoClickConfirmation } from '../utils/twoClickConfirmation';
import { JournalCheckStatus } from '../utils/journalCheckStatus';
import { showJournalDisabled, showJournalTemplates, type JournalTabElements } from '../ui/journalTabView';
import { readSelectedFormula, insertEntriesFormula } from '../services/EntriesDrilldownService';
import type { SelectedFormula, InsertTarget } from '../services/EntriesDrilldownService';
import { canFinalizeAndPost, isRightsRefusal, journalPermissionNote, ownJournalUnderReview } from '../utils/journalPermissions';
import { connectionForNewJournal, sheetPostsAtOnce } from '../utils/journalSendRoute';
import { alreadyPostedMessage, CHECKING_MESSAGE, EDITED_SINCE_CHECK_MESSAGE, errorListHeading, errorLocation, errorsForDisplay, FINALIZE_POSTS_AT_ONCE_MESSAGE, JOURNAL_BUSY_MESSAGE, listNoteMessage, listNotesFoundMessage, listsRefreshedMessage, OWN_JOURNAL_UNDER_REVIEW_NOTE, POSTS_AT_ONCE_WARNING, sentMessage, sheetMisfitMessage, SWITCHING_COMPANY_MESSAGE } from '../utils/journalMessages';
import type { Formula } from './FormulaBuilder';
import { activeCompanyId, formulaCallName, isUnreachable, needsPrefix, type FormulaCompany } from './formulaPrefix';
import type { JournalJob, JournalTemplate, ResolvedJournalSheet, ValidationError, JournalPermissions } from '../types/journal';
import type { AgeingSettings, SavedView, ViewsClient } from './EntriesViewPanel';

interface AliasData {
  id: string;
  alias: string;
  erpCode: string;
}

interface CompanyWithFormulas {
  companyId: string;
  companyName: string;
  shortName: string | null;
  type: 'primary' | 'external' | 'demo';
  industryType: string | null;
  formulas: Formula[];
  /** Set on load: whether this is the session's active company (formulaPrefix.ts). */
  isActive?: boolean;
}

/**
 * Custom functions share the task-pane runtime (manifest `lifetime="long"`), so
 * until that runtime starts, no BIFROST formula can resolve and Excel parks the
 * cells at #BUSY with no way to recover. Opting the document into load-on-open
 * means reopening it is enough — the user no longer has to remember to open the
 * pane first.
 *
 * Per-document and only effective once the add-in has run in that document, so
 * this never slows down workbooks that don't use Bifrost. It also cannot help
 * the very first open of a brand-new file; that one still needs the pane.
 */
async function enableLoadOnDocumentOpen(): Promise<void> {
  try {
    if (!Office.addin?.setStartupBehavior) return;
    await Office.addin.setStartupBehavior(Office.StartupBehavior.load);
  } catch (error) {
    // Older hosts and any client without SharedRuntime 1.1 simply keep the
    // open-the-pane-first behaviour. Never block startup over it.
    debugLog('Could not enable load-on-document-open:', error);
  }
}

/**
 * Ribbon ExecuteFunction handlers.
 *
 * The manifest's buttons resolve their function names against the page named by
 * <FunctionFile>, which under a shared runtime is this task pane. The
 * implementations live inside initialize(), so the ribbon calls through this
 * registry rather than owning a second copy of the logic.
 */
interface RibbonActions {
  recalc: (scope: 'sheet' | 'workbook', options?: { forceRefresh?: boolean }) => Promise<void>;
  hardcode: (scope: HardcodeScope) => void;
  /** Open one of the pane's tabs. */
  goTo: (view: 'formulas' | 'entries' | 'aliases' | 'journal') => void;
  /** Open the dashboard at its connection section. */
  openConnection: () => void;
  clearCache: () => Promise<void>;
  prepareForClaude: () => void;
  openExternal: (url: string, label: string) => Promise<void>;
}

let ribbonActions: RibbonActions | null = null;

/** Drop the add-in's cached formula results, so the next calculation goes to the server. */
async function dropCachedResults(): Promise<void> {
  const { clearPreCalculatedResults } = await import('../functions/CustomFunctions');
  await clearPreCalculatedResults();
}

/** Pull the pane into view — ribbon actions are useless if their UI is hidden. */
async function showTaskpane(): Promise<void> {
  try {
    await Office.addin?.showAsTaskpane?.();
  } catch (error) {
    // Not fatal: the action still runs, the user just has to open the pane.
    console.warn('Could not open the task pane:', error);
  }
}

function ribbonAction(
  name: string,
  run: (actions: RibbonActions) => void | Promise<void>
): void {
  Office.actions.associate(name, (event: Office.AddinCommands.Event) => {
    void (async () => {
      try {
        if (ribbonActions) await run(ribbonActions);
      } catch (error) {
        console.error(`Ribbon action '${name}' failed:`, error);
      } finally {
        // Office keeps the button spinning until the event is completed.
        event.completed();
      }
    })();
  });
}

/**
 * Enable or disable the ribbon's working controls.
 *
 * Toggling availability is the only state the ribbon can express — labels are
 * static manifest strings — so this is what stops Recalculate and Hardcode
 * looking clickable while the user is signed out and silently doing nothing.
 * Office decides when it repaints, so treat this as a request, not a write.
 */
async function updateRibbon(
  groups: Array<{ id: string; controls: Array<{ id: string; enabled: boolean }> }>
): Promise<void> {
  if (!Office.ribbon?.requestUpdate) return;
  try {
    await Office.ribbon.requestUpdate({
      tabs: [{ id: `BifrostToolsTab${RIBBON_ID_SUFFIX}`, groups }],
    });
  } catch (error) {
    // HostRestartNeeded after an upgrade that changed the command set, or an
    // older host without RibbonApi 1.1. Neither is worth interrupting anyone
    // over: the controls just stay as they are.
    console.warn('Could not update ribbon availability:', error);
  }
}

async function setRibbonState({ signedIn, isAdmin }: { signedIn: boolean; isAdmin: boolean }): Promise<void> {
  await updateRibbon([
    {
      id: `BifrostMainGroup${RIBBON_ID_SUFFIX}`,
      // Portal ships disabled in the manifest, so this is the only thing that
      // ever turns it on — fail closed for everyone who isn't an admin.
      controls: [
        { id: 'StatusButton', enabled: signedIn },
        { id: 'PortalButton', enabled: signedIn && isAdmin },
      ],
    },
    {
      id: `BifrostFormulasGroup${RIBBON_ID_SUFFIX}`,
      controls: [
        { id: 'InsertFormulaButton', enabled: signedIn },
        { id: 'AliasesButton', enabled: signedIn },
      ],
    },
    {
      id: `BifrostCalculateGroup${RIBBON_ID_SUFFIX}`,
      // Only RecalculateMenu lives here. Naming a control the manifest does not
      // declare makes Office reject the update with ControlIdNotFound, which can
      // take the rest of the payload with it — and this is the only call that
      // ever enables the ribbon after sign-in.
      controls: [{ id: 'RecalculateMenu', enabled: signedIn }],
    },
    {
      id: `BifrostPublishGroup${RIBBON_ID_SUFFIX}`,
      // Journal is left alone here: its availability follows the company's
      // journal policy, not the session, and setJournalEnabled owns it.
      controls: [{ id: 'HardcodeMenu', enabled: signedIn }],
    },
  ]);
}

/** Mirrors the Journal nav tab: shown only where the company allows it. */
async function setJournalEnabled(enabled: boolean): Promise<void> {
  await updateRibbon([
    {
      id: `BifrostPublishGroup${RIBBON_ID_SUFFIX}`,
      controls: [{ id: 'JournalButton', enabled }],
    },
  ]);
}

function registerRibbonActions(): void {
  if (!Office.actions?.associate) return;

  // Every one of these reports into the task pane, so bring it forward first —
  // a ribbon press that works silently behind a closed or unrelated pane is
  // indistinguishable from one that did nothing.
  ribbonAction('recalculateSheet', async a => { await showTaskpane(); await a.recalc('sheet'); });
  ribbonAction('recalculateWorkbook', async a => { await showTaskpane(); await a.recalc('workbook'); });
  ribbonAction('recalculateWorkbookFresh', async a => {
    await showTaskpane();
    await a.recalc('workbook', { forceRefresh: true });
  });

  // These three open the pane's dialog rather than acting immediately: the
  // ribbon is a shortcut into the confirm + preview, not a way around it.
  ribbonAction('hardcodeSelection', async a => { await showTaskpane(); a.hardcode('selection'); });
  ribbonAction('hardcodeSheet', async a => { await showTaskpane(); a.hardcode('sheet'); });
  ribbonAction('hardcodeWorkbook', async a => { await showTaskpane(); a.hardcode('workbook'); });

  // Pure navigation into a pane tab.
  ribbonAction('insertFormula', async a => { await showTaskpane(); a.goTo('formulas'); });
  ribbonAction('openEntries', async a => { await showTaskpane(); a.goTo('entries'); });
  ribbonAction('openAliases', async a => { await showTaskpane(); a.goTo('aliases'); });
  ribbonAction('openStatus', async a => { await showTaskpane(); a.openConnection(); });
  ribbonAction('openJournal', async a => { await showTaskpane(); a.goTo('journal'); });

  ribbonAction('clearFormulaCache', async a => { await showTaskpane(); await a.clearCache(); });
  ribbonAction('prepareForClaude', async a => { await showTaskpane(); a.prepareForClaude(); });

  ribbonAction('openHelp', a => a.openExternal(HELP_URL, 'the documentation'));
  ribbonAction('openPortal', a => a.openExternal(PORTAL_URL, 'the Bifrost portal'));
}

const SSO_FALLBACK_MESSAGE = 'Microsoft sign-in did not work here. Sign in with your email and password instead.';

/**
 * SSOService reports its own failures in developer terms ("Could not get
 * Office access token"); those become one plain message. Anything else is the
 * backend's, which is already written for the user ("Joining your company
 * needs your work Microsoft account…") and is shown as is.
 */
const SSO_INTERNAL_ERRORS: Record<string, string> = {
  'SSO not supported in this environment': SSO_FALLBACK_MESSAGE,
  'Could not get Office access token': SSO_FALLBACK_MESSAGE,
  'Backend sync failed': SSO_FALLBACK_MESSAGE,
  'Token exchange failed': SSO_FALLBACK_MESSAGE,
  'SSO failed': SSO_FALLBACK_MESSAGE,
  'Terms not accepted': 'Microsoft sign-in cancelled. Accept the terms to continue.',
  'Company registration cancelled': 'Microsoft sign-in cancelled.',
  // ApiService maps any backend message mentioning "sign in" to this cell
  // text, which on the sign-in screen says nothing.
  [CellError.AUTH_FAILED]: SSO_FALLBACK_MESSAGE,
};

function ssoErrorMessage(raw: string | undefined): string {
  if (!raw) return SSO_FALLBACK_MESSAGE;
  return SSO_INTERNAL_ERRORS[raw] ?? raw;
}

const LICENSE_LABELS: Record<Exclude<LicenseCheck['state'], 'unknown'>, string> = {
  active: 'Active',
  trial: 'Trial',
  expired: 'Expired',
  none: 'No active plan',
};

const ERP_LABELS: Record<string, string> = {
  TRIPLETEX: 'Tripletex',
  BUSINESS_CENTRAL: 'Business Central',
  DYNAMICS_365: 'Dynamics 365',
};

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super admin',
  ADMIN: 'Admin',
  USER: 'User',
};

/** The role enum as a person reads it: `SUPER_ADMIN` becomes "Super admin". */
function roleLabel(role: string | undefined): string {
  if (!role) return ROLE_LABELS.USER;
  if (ROLE_LABELS[role]) return ROLE_LABELS[role];
  const words = role.toLowerCase().replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Last-resort reporting. A throw anywhere in startup used to leave the pane
 * showing nothing but its header — no view is visible until initialize() picks
 * one, so a blank pane is indistinguishable from a hung one.
 */
function reportStartupFailure(error: unknown): void {
  console.error('Bifrost: task pane failed to start:', error);
  const body = document.getElementById('app-body');
  if (!body) return;
  const message = error instanceof Error ? error.message : String(error);
  body.insertAdjacentHTML('afterbegin',
    `<div class="startup-error" role="alert">
       <p><strong>Bifrost Ledger could not start.</strong></p>
       <p>${message.replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch] as string))}</p>
       <p>Close and reopen the task pane. If it keeps happening, send us this message.</p>
     </div>`);
}

Office.onReady((info) => {
  if (info.host !== Office.HostType.Excel) return;

  void enableLoadOnDocumentOpen();

  // Formula results are held in memory only; clean up what an earlier build
  // left on disk.
  void import('../functions/CustomFunctions').then(m => m.purgePersistedPreCalc()).catch(() => {});

  // Ribbon wiring is deliberately isolated: it is a nice-to-have next to the
  // pane itself, and having it in front of initialize() meant one bad
  // associate() call took the entire pane down with it.
  try {
    registerRibbonActions();
  } catch (error) {
    console.error('Bifrost: ribbon actions could not be registered:', error);
  }

  const start = () => {
    try {
      const result = initialize();
      if (result && typeof result.catch === 'function') result.catch(reportStartupFailure);
    } catch (error) {
      reportStartupFailure(error);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
});

async function initialize() {
  const performanceManager = PerformanceManager.getInstance();

  performanceManager.measure('taskpane-init', () => {
    debugLog('Bifrost: Initializing task pane...');
  });

  const authService = new AuthService();
  const tokenManager = new TokenManager();
  const viewManager = new ViewManager();
  const accessibilityManager = AccessibilityManager.getInstance();
  const apiService = new ApiService();
  const deviceFingerprintService = new DeviceFingerprint();
  const hardcodeService = new HardcodeService();

  const loginBtn = document.getElementById('login-btn') as HTMLButtonElement;
  const emailInput = document.getElementById('email') as HTMLInputElement;
  const passwordInput = document.getElementById('password') as HTMLInputElement;
  const loginError = document.getElementById('login-error');
  const loginLoading = document.getElementById('login-loading');
  const logoutBtn = document.getElementById('logout-btn');
  const refreshTokenBtn = document.getElementById('refresh-token-btn');

  // Formula builder
  const browseContainer = document.getElementById('formulas-browse');
  const buildContainer = document.getElementById('formulas-build');
  let formulaBuilder: FormulaBuilder | null = null;
  let cachedFormulas: Formula[] = [];
  let formulasLoaded = false;
  let formulaSearchWired = false;

  if (buildContainer) {
    formulaBuilder = new FormulaBuilder(buildContainer, () => {
      if (browseContainer) browseContainer.style.display = 'block';
      if (buildContainer) buildContainer.style.display = 'none';
    });
  }

  // The Entries tab's catalogue opens the same builder, writing ENTRIES, so
  // each argument is named and numbered there too.
  const entriesBuildContainer = document.getElementById('entries-build');
  // Saved Entries views for customer and vendor formulas (EntriesViewPanel).
  const viewsClient: ViewsClient = {
    list: async formulaId => {
      const res = await apiService.get<{ defaults: { ageing: AgeingSettings }; views: SavedView[] }>(
        `/formulas/${encodeURIComponent(formulaId)}/views`, { headers: await getAuthHeaders() });
      return res.success && res.data ? res.data : null;
    },
    create: async (formulaId, name, settings) => {
      const res = await apiService.post(`/formulas/${encodeURIComponent(formulaId)}/views`, { name, settings }, { headers: await getAuthHeaders() });
      return res.success ? { ok: true } : { ok: false, error: res.error || 'The view could not be saved' };
    },
  };
  const entriesBuilder = entriesBuildContainer
    ? new FormulaBuilder(entriesBuildContainer, () => closeEntriesBuilder(), 'entries', viewsClient)
    : null;

  function closeEntriesBuilder(): void {
    if (entriesBuildContainer) entriesBuildContainer.style.display = 'none';
    const catalogue = document.getElementById('entries-catalogue');
    if (catalogue) catalogue.style.display = 'block';
  }

  // Both add-ins can be installed side by side; say which build this pane is.
  const brandEnv = document.getElementById('brand-env');
  if (brandEnv && ADDIN_NAMESPACE !== 'BIFROST') {
    brandEnv.textContent = ADDIN_NAMESPACE.replace('BIFROST_', '');
  }

  setupNavigation(viewManager);

  const companySwitcher = new CompanySwitcher(async (target) => {
    await switchCompany(target);
  });

  const workbookPanel = new WorkbookCompanyPanel({
    currentCompany: async () => {
      // The stored user outlives a session the pane has given up on.
      if (viewManager.getCurrentView() === 'login') return null;
      const user = await tokenManager.getUserInfo();
      if (!user?.companyId) return null;
      companySwitcher.showCurrent(user.companyId);
      return { companyId: user.companyId, companyName: user.company || companySwitcher.listed(user.companyId)?.name || '' };
    },
    reachable: (companyId) => {
      const company = companySwitcher.listed(companyId);
      return company ? { id: company.id, name: company.name } : null;
    },
    mayBelongElsewhere: (companyId) => companySwitcher.mayBelongElsewhere(companyId),
    switchTo: (target) => switchCompany(target),
    recalculate: () => duringPaneAction(() => recalculateThisWorkbook()),
    // Moving the workbook recalculates it, which must not cut across a journal action or a switch.
    blocked: () => (journalGuard.switchingCompany ? SWITCHING_COMPANY_MESSAGE : journalGuard.busy ? JOURNAL_BUSY_MESSAGE : null),
    report: (message) => viewManager.notify(message, 'error'),
    notice: (message) => viewManager.notify(message, 'info'),
  });
  workbookPanel.watch();

  // isAuthenticated() renews an aged-out access token from the refresh token,
  // so a pane reopened after an hour does not need SSO or a password.
  if (await authService.isAuthenticated()) {
    viewManager.showView('dashboard');
    await showAuthenticatedView();
  } else {
    debugLog('Attempting silent SSO authentication...');
    const ssoResult = await authService.trySilentSSO();

    if (ssoResult.success && ssoResult.token) {
      debugLog('Silent SSO successful');
      viewManager.showView('dashboard');
      await showAuthenticatedView();
    } else {
      debugLog('Silent SSO not available:', ssoResult.error);
      viewManager.showView('login');
    }
  }

  // Login form submission
  // After an explicit sign-in, recalculate this workbook so any custom-function
  // cells that resolved to #SIGN_IN while unauthenticated re-evaluate and pick
  // up the freshly stored token; the cells are not otherwise told that auth
  // state changed. Another open workbook's runtime sees the new session itself
  // and recalculates its own cells left on #SIGN_IN (signInRecovery). A
  // workbook that belongs to another company than the sign-in landed on, or
  // whose company cannot be read, keeps its saved figures; the pane offers the
  // switch.
  async function recalcCustomFunctionsAfterSignIn(signInStartedAt: number): Promise<void> {
    try {
      void workbookPanel.refresh();
      const user = await tokenManager.getUserInfo();
      await officeReady();
      if (!mayRecalculateFor(user?.companyId)) return;
      // This runtime's own look for a new session may have recalculated every cell already.
      if (allCellsSetOffSince(signInStartedAt)) return;
      await duringPaneAction(() => recalculateThisWorkbook());
    } catch (error) {
      // Non-critical: cells refresh on the next manual recalc.
      console.warn('Post-sign-in recalculation failed:', error);
    }
  }

  const handleLogin = performanceManager.debounce(async () => {
    const email = emailInput.value.trim();
    const password = passwordInput.value.trim();

    if (!email || !password) {
      showError('Please enter both email and password');
      return;
    }

    try {
      showLoading(true);
      hideError();
      const signInStartedAt = Date.now();

      const authResult = await performanceManager.measureAsync('login-process', async () => {
        return await authService.login(email, password);
      });

      if (!authResult) {
        showError('Login failed - no response from server');
        return;
      }

      if (authResult.success && authResult.token && authResult.user) {
        await tokenManager.storeToken(authResult.token, authResult.user);

        performanceManager.requestAnimationFrame(() => {
          viewManager.showView('dashboard');
        });

        await showAuthenticatedView();
        await recalcCustomFunctionsAfterSignIn(signInStartedAt);
        accessibilityManager.announceSuccess('Successfully logged in');
      } else {
        showError(authResult.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      showError('An error occurred during login. Please try again.');
    } finally {
      showLoading(false);
    }
  }, 300, 'login-submit');

  loginBtn?.addEventListener('click', handleLogin);

  // A plain target="_blank" opens a new tab on the web, where the click is
  // the user gesture the popup needs. Desktop hosts get openBrowserWindow,
  // which that API does not offer on the web.
  const learnMoreLink = document.getElementById('login-learn-more') as HTMLAnchorElement | null;
  if (learnMoreLink) {
    learnMoreLink.href = HELP_URL;
    learnMoreLink.addEventListener('click', (event) => {
      if (!Office.context.ui?.openBrowserWindow) return;
      event.preventDefault();
      Office.context.ui.openBrowserWindow(HELP_URL);
    });
  }

  // SSO button
  const ssoBtn = document.getElementById('sso-btn') as HTMLButtonElement;
  ssoBtn?.addEventListener('click', async () => {
    try {
      showLoading(true);
      hideError();
      const signInStartedAt = Date.now();
      const ssoResult = await authService.trySSO();

      if (ssoResult.success && ssoResult.token) {
        viewManager.showView('dashboard');
        await showAuthenticatedView();
        await recalcCustomFunctionsAfterSignIn(signInStartedAt);
        accessibilityManager.announceSuccess('Successfully signed in with Microsoft');
      } else {
        console.warn('Microsoft sign-in failed:', ssoResult.error);
        showError(ssoErrorMessage(ssoResult.error));
      }
    } catch (error) {
      console.error('SSO error:', error);
      showError('Microsoft sign-in failed. Please try email/password.');
    } finally {
      showLoading(false);
    }
  });

  // Enter key for login
  [emailInput, passwordInput].forEach(input => {
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') loginBtn.click();
    });
  });

  // Logout
  logoutBtn?.addEventListener('click', async () => {
    await tokenManager.clearToken();
    viewManager.showView('login');
    showLoginView();
  });

  // Refresh — re-fetch permissions + company + sandbox status + active tab's
  // data, without forcing the user through login again. Useful after an admin
  // enables/disables features, changes the active ERP, or toggles journal
  // policy. Attempts a token refresh too, but a missing refresh token (e.g.
  // SSO sessions) is NOT a reason to log out as long as the access token is
  // still valid.
  const REFRESH_LABEL = 'Refresh';
  // The button is a bare text link now, so its own node carries the label —
  // there is no inner .ms-Button-label span to write into any more.
  const labelEl = refreshTokenBtn;
  let resetLabelTimeout: ReturnType<typeof setTimeout> | null = null;
  const setLabel = (text: string, resetAfterMs?: number): void => {
    if (!labelEl) return;
    labelEl.textContent = text;
    if (resetLabelTimeout) clearTimeout(resetLabelTimeout);
    if (resetAfterMs) {
      resetLabelTimeout = setTimeout(() => {
        if (labelEl) labelEl.textContent = REFRESH_LABEL;
      }, resetAfterMs);
    }
  };

  refreshTokenBtn?.addEventListener('click', () => duringPaneAction(async () => {
    // A refresh reads the Journal tab again, which would swap the session under a running journal action.
    if (journalGuard.busy) {
      viewManager.notify(JOURNAL_BUSY_MESSAGE, 'info');
      return;
    }
    setLabel('Refreshing…');
    refreshTokenBtn.setAttribute('disabled', 'true');

    try {
      // isAuthenticated() renews silently when the access token has aged out
      // but the refresh token is still good — only a genuinely dead session
      // falls through to the login view. Checking the access token alone sent
      // users to sign-in once an hour even though the session was renewable.
      if (!await authService.isAuthenticated()) {
        viewManager.showView('login');
        showLoginView();
        return;
      }
      // Again: a journal action may have started while the session was renewed.
      if (journalGuard.busy) {
        viewManager.notify(JOURNAL_BUSY_MESSAGE, 'info');
        setLabel(REFRESH_LABEL);
        return;
      }

      await redrawPane();

      // Reload the currently-active tab's data.
      const activeView = viewManager.getCurrentView();
      if (activeView === 'formulas') {
        await loadFormulas();
      } else if (activeView === 'aliases') {
        await loadAliases();
      } else if (activeView === 'journal') {
        await journalGuard.load();
      }

      setLabel('Refreshed', 1500);
    } catch (error) {
      console.error('Refresh error:', error);
      setLabel('Refresh failed', 2000);
    } finally {
      refreshTokenBtn.removeAttribute('disabled');
    }
  }));

  // Refresh formulas button
  const refreshFormulasBtn = document.getElementById('refresh-formulas-btn');
  refreshFormulasBtn?.addEventListener('click', async () => {
    formulasLoaded = false;
    await loadFormulas();
  });

  // Dashboard action buttons
  setupDashboardActions();

  // Journal setup
  const journalSvc = new JournalService(apiService);
  let journalTemplates: JournalTemplate[] = [];
  let selectedTemplate: JournalTemplate | null = null;
  let activeJournalSheet: ResolvedJournalSheet | null = null;
  let journalLoaded = false;
  /** Whether the company the Journal tab was last read for has Journal turned on. */
  let journalEnabled = false;
  const journalGuard = new JournalPaneGuard({
    readTab: readJournalTab,
    signedInAs: () => tokenManager.getUserInfo(),
    redrawPane,
    refused: journalRefused,
    notice: message => viewManager.notify(message, 'info'),
  });
  const journalEdits = new JournalEditWatcher(
    {
      manifestOf: worksheetId => journalSvc.manifestOf(worksheetId),
      markListValues: (worksheetId, address, template, headerRow) => journalSvc.markListValues(worksheetId, address, template, headerRow),
      headerFit: (sheet, template) => journalSvc.headerFit(sheet, template),
      listOwner: () => journalSvc.listOwner(),
      // Renews a token the pane may have let age since the last click, without sending a user who
      // is typing to the sign-in page: a re-check is not worth that.
      refreshListsFor: async (template, sheet, notes, owner) => {
        const headers = await quietAuthHeaders();
        if (!headers) return false;
        // The renewed session may belong to another company than the edit's lists, after a switch or
        // a revoked access: its lists must not be written under the edit's company.
        const user = await tokenManager.getUserInfo();
        if (ownerScope(user?.companyId, user?.userId) !== owner.scope) return false;
        return journalSvc.refreshListsFor(template, sheet, notes, { ...owner, headers });
      },
      recheckMarks: (worksheetId, address, template, headerRow, asked) => journalSvc.recheckMarks(worksheetId, address, template, headerRow, asked),
    },
    templateId => journalTemplates.find(t => t.id === templateId),
    (notes, template, found) => {
      if (!found) {
        showWbStatus(listNoteMessage(notes, template), 'error');
        statusIsListNote = true;
        return;
      }
      // Only over the edit's own note: a check result or an ERP reference shown since stays.
      if (statusIsListNote) {
        showWbStatus(listNotesFoundMessage(found, notes, template), notes.length > 0 ? 'error' : 'success');
        statusIsListNote = notes.length > 0;
      }
    },
  );

  const templateDropdown = document.getElementById('journal-template-dropdown') as HTMLSelectElement;
  const journalActionsDiv = document.getElementById('journal-actions') as HTMLElement;
  const journalStatusDiv = document.getElementById('journal-status') as HTMLElement;
  const journalStatusMsg = document.getElementById('journal-status-message') as HTMLElement;
  const journalErrorsDiv = document.getElementById('journal-errors') as HTMLElement;
  const journalErrorList = document.getElementById('journal-error-list') as HTMLElement;
  const journalErrorsHeading = document.getElementById('journal-errors-heading');
  const journalPermissionNoteDiv = document.getElementById('journal-permission-note') as HTMLElement | null;
  const journalEmptyHint = document.getElementById('journal-empty-hint');

  // The access token ages out hourly; isAuthenticated() renews it. When renewal
  // fails the session is over, and the pane goes to sign-in rather than keep
  // showing a dashboard it can no longer fetch for.
  async function getValidToken(): Promise<string | null> {
    if (await authService.isAuthenticated()) {
      const token = await tokenManager.getToken();
      if (token) return token;
    }
    viewManager.showView('login');
    showLoginView();
    return null;
  }

  viewManager.setSessionEndedHandler(() => {
    viewManager.showView('login');
    showLoginView();
  });

  /** Fresh auth headers, or null when the session cannot be renewed; never sends the user to sign in. */
  async function quietAuthHeaders(): Promise<Record<string, string> | null> {
    try {
      if (!await authService.isAuthenticated()) return null;
      const token = await tokenManager.getToken();
      if (!token) return null;
      return { Authorization: `Bearer ${token}`, 'x-device-fingerprint': await deviceFingerprintService.generate() };
    } catch {
      return null;
    }
  }

  async function getAuthHeaders(): Promise<Record<string, string>> {
    const token = await getValidToken();
    if (!token) throw new Error('Not authenticated');
    const fingerprint = await deviceFingerprintService.generate();
    return { Authorization: `Bearer ${token}`, 'x-device-fingerprint': fingerprint };
  }

  /** Whether the status line shows a note about values their lists lack, which a re-check may replace. */
  let statusIsListNote = false;
  const checkStatus = new JournalCheckStatus();

  function showWbStatus(message: string, type: 'success' | 'error' | 'info') {
    statusIsListNote = false;
    checkStatus.cleared();
    if (!journalStatusDiv || !journalStatusMsg) return;
    journalStatusDiv.style.display = 'block';
    journalStatusMsg.textContent = message;
    const colors = { error: ['#FDE7E9', '#A4262C'], success: ['#DFF6DD', '#107C10'], info: ['#F3F2F1', '#323130'] };
    const [bg, fg] = colors[type];
    journalStatusMsg.style.background = bg;
    journalStatusMsg.style.color = fg;
  }

  function showWbErrors(errors: ValidationError[]) {
    if (!journalErrorsDiv || !journalErrorList) return;
    journalErrorsDiv.style.display = 'block';
    if (journalErrorsHeading) journalErrorsHeading.textContent = errorListHeading(errors);
    // Messages carry cell values and the ERP's own wording, so they go in as
    // text, never as markup. appendChild, not append or replaceChildren: the
    // desktop ES5 fallback has neither.
    journalErrorList.textContent = '';
    for (const e of errorsForDisplay(errors)) {
      const row = document.createElement('div');
      row.style.cssText = 'padding:4px 0;border-bottom:1px solid #eee;';
      const label = document.createElement('strong');
      label.textContent = errorLocation(e, selectedTemplate);
      row.appendChild(label);
      row.appendChild(document.createTextNode(`: ${e.message}`));
      journalErrorList.appendChild(row);
    }
  }

  function hideWbErrors() {
    if (journalErrorsDiv) journalErrorsDiv.style.display = 'none';
    if (journalErrorList) journalErrorList.innerHTML = '';
  }

  const pushBtn = document.getElementById('journal-push-btn') as HTMLButtonElement | null;
  const postBtn = document.getElementById('journal-post-btn') as HTMLButtonElement | null;
  const validateBtn = document.getElementById('journal-validate-btn') as HTMLButtonElement | null;
  const insertBtn = document.getElementById('journal-insert-btn') as HTMLButtonElement | null;
  const submitBtn = document.getElementById('journal-submit-btn') as HTMLButtonElement | null;
  const journalTab: JournalTabElements = {
    templateDropdown, insertBtn, validateBtn, pushBtn, postBtn, permissionNote: journalPermissionNoteDiv, emptyHint: journalEmptyHint,
  };

  // Toggle the Journal nav tab visibility based on the company's journal
  // policy. Called after login + after a company switch so the tab reflects
  // the active company's permissions, not the previous one.
  async function refreshJournalTabVisibility(): Promise<void> {
    // Resolved on each call, not captured once at init.
    //
    // It used to be a `const` further down this function, while
    // showAuthenticatedView() — a hoisted declaration — is CALLED near the top
    // of initialize(), before that line runs. Reading it there hit the temporal
    // dead zone and threw "Cannot access 'journalNavBtn' before
    // initialization". The throw happened before the try below, so it
    // propagated into the caller's catch and vanished, and the tab stayed
    // hidden for the whole session. Clicking Refresh worked only because that
    // handler fires long after initialize() has finished.
    const journalNavBtn = document.getElementById('nav-btn-journal') as HTMLButtonElement | null;
    if (!journalNavBtn) return;
    try {
      const headers = await getAuthHeaders();
      const permResponse = await apiService.get<{ companyJournalEnabled: boolean }>(
        '/journal/permissions', { headers }
      );
      // A failed read leaves the tab as it is, as the catch below does: it is not Journal turned off.
      if (!permResponse.success || !permResponse.data) return;
      const enabled = permResponse.data.companyJournalEnabled === true;
      journalNavBtn.style.display = enabled ? '' : 'none';
      void setJournalEnabled(enabled);
      // If the user is currently on the journal view but it just became
      // unavailable (company switch), bounce them back to dashboard.
      if (!enabled && viewManager.getCurrentView() === 'journal') {
        viewManager.showView('dashboard');
      }
    } catch (error) {
      // Leave the tab as it is. Hiding it here made a transient network or
      // token error indistinguishable from the company having Journal switched
      // off, and it stayed hidden until something else re-ran this.
      console.error('Failed to refresh journal tab visibility:', error);
    }
  }

  /**
   * Submitting only makes sense once the journal has validated cleanly, so the
   * button stays inert until a validation run passes and goes inert again the
   * moment anything could have changed.
   */
  function setSubmitEnabled(enabled: boolean): void {
    if (!submitBtn) return;
    submitBtn.disabled = !enabled;
    submitBtn.title = enabled ? '' : 'Check for errors first';
  }

  /**
   * Hide actions the user cannot perform instead of greying them out — a
   * permanently disabled button reads as broken, and its tooltip is all but
   * invisible in Excel on the web. The backend remains the actual gate.
   */
  let erpHasDraftState = false;
  let journalPerms: JournalPermissions | null = null;
  /** Asks for a second click before a push that posts at once and cannot be undone. */
  const pushConfirmation = new TwoClickConfirmation();
  const journalActions = new JournalActionRunner(journalGuard, {
    prepare: prepareJournalService,
    actionStarting: () => pushConfirmation.reset(),
    // A template picked while an action runs would move the sheet it works on.
    running: running => {
      if (templateDropdown) templateDropdown.disabled = running || journalTemplates.length === 0;
    },
  });

  function applyJournalPermissions(perms: JournalPermissions): void {
    journalPerms = perms;
    // Absent counts as "no draft stage", matching the backend's default. The
    // cost of being wrong that way is one extra confirmation; the other way it
    // is a posted ledger entry the user believed was a draft.
    erpHasDraftState = perms.erpHasDraftState === true;

    if (pushBtn) {
      pushBtn.style.display = perms.canPush ? '' : 'none';
      // Without a draft stage this button is the posting, so it must not offer
      // to save a draft.
      pushBtn.textContent = erpHasDraftState ? 'Save to ERP as draft' : 'Post to ERP';
    }
    if (postBtn) postBtn.style.display = canFinalizeAndPost(perms) ? '' : 'none';
    applyTemplateApproval();
  }

  /**
   * Someone who can push sends their own journal, so only a preparer hands it
   * on — except on a template that requires review, where everyone sends for
   * review and push stays visible for a colleague sending someone else's journal.
   */
  function applyTemplateApproval(): void {
    if (!journalPerms) return;
    const approvalRequired = selectedTemplate?.approvalRequired === true;
    if (submitBtn) submitBtn.style.display = journalPerms.canPush && !approvalRequired ? 'none' : '';
    setSubmitEnabled(false);

    void applyOwnJournalReview();
  }

  /**
   * On a template that requires review, greys out Push and Finalize & post for whoever prepared the
   * active sheet's journal or sent it for review, and says why beside the rights note. The backend
   * refuses them either way; this only spares them a click that cannot work.
   */
  async function applyOwnJournalReview(): Promise<void> {
    if (!journalPerms) return;
    const user = await tokenManager.getUserInfo().catch(() => null);
    const own = ownJournalUnderReview(selectedTemplate, activeJournalSheet?.manifest, user?.userId);
    for (const button of [pushBtn, postBtn]) {
      if (!button) continue;
      button.disabled = own;
      button.title = own ? OWN_JOURNAL_UNDER_REVIEW_NOTE : '';
    }
    const note = [journalPermissionNote(journalPerms, selectedTemplate?.approvalRequired === true), own ? OWN_JOURNAL_UNDER_REVIEW_NOTE : '']
      .filter(Boolean).join(' ');
    if (journalPermissionNoteDiv) {
      journalPermissionNoteDiv.textContent = note;
      journalPermissionNoteDiv.style.display = note ? 'block' : 'none';
    }
  }

  /** The sheet the Journal tab acts on; whose journal it is decides whether Push and Finalize are greyed out. */
  async function useJournalSheet(sheet: ResolvedJournalSheet | null): Promise<void> {
    activeJournalSheet = sheet;
    await applyOwnJournalReview();
  }

  /**
   * Reads the rights again and shows or hides the buttons by them. The refusal message stays, and so
   * does Submit's state: nothing on the sheet changed.
   */
  async function reloadJournalPermissions(): Promise<void> {
    try {
      const headers = await getAuthHeaders();
      // Rights read for another company or user are not this tab's; the next click draws the pane again.
      if (!journalGuard.holds(await identityOf(headers))) return;
      const response = await apiService.get<JournalPermissions>('/journal/permissions', { headers });
      const perms = response.data;
      if (!perms) return;
      if (!perms.companyJournalEnabled) {
        await journalGuard.load();
        return;
      }
      const submitWasEnabled = submitBtn ? !submitBtn.disabled : false;
      applyJournalPermissions(perms);
      setSubmitEnabled(submitWasEnabled);
    } catch (error) {
      console.error('Failed to reload journal permissions:', error);
    }
  }

  /**
   * Who the journal calls carrying `headers` go out as: the company and user in its token, with the
   * names the add-in stored for them.
   */
  async function identityOf(headers: Record<string, string>): Promise<UserInfo> {
    const stored = await tokenManager.getUserInfo();
    let claims: { userId?: string; companyId?: string; email?: string } = {};
    try {
      claims = tokenManager.decodeJWT((headers.Authorization ?? '').replace(/^Bearer /, '')) as typeof claims;
    } catch {
      return stored ?? { email: '', userId: '', permissions: [] };
    }
    const companyId = claims.companyId ?? stored?.companyId;
    return {
      email: claims.email ?? stored?.email ?? '',
      userId: claims.userId ?? stored?.userId ?? '',
      companyId,
      company: stored?.companyId === companyId ? stored?.company : undefined,
      permissions: stored?.permissions ?? [],
    };
  }

  /** What the Journal tab shows for whoever `headers` carries. Throws when it could not be read. */
  function readJournalTabFor(headers: Record<string, string>): Promise<JournalTabData> {
    return readJournalTabData({
      identity: () => identityOf(headers),
      permissions: () => apiService.get<JournalPermissions>('/journal/permissions', { headers }),
      templates: () => journalSvc.fetchTemplates(headers),
      isAdmin: isAdminUser,
    });
  }

  /**
   * Reads the Journal tab's rights and templates for whoever is signed in, and shows them only while
   * `current()` holds: a reset or a newer read since means this one shows nothing. Answers who it read
   * them for, or null when it showed nothing.
   */
  async function readJournalTab(current: () => boolean): Promise<UserInfo | null> {
    try {
      const headers = await getAuthHeaders();
      const read = await readJournalTabFor(headers);
      if (!current()) return null;

      journalSvc.setAuthHeaders(headers);
      journalSvc.setListOwner(read.user.companyId ?? '', read.user.userId);
      journalEnabled = read.enabled;
      journalTemplates = read.templates;
      selectedTemplate = null;
      activeJournalSheet = null;
      clearJournalActionState();
      if (journalActionsDiv) journalActionsDiv.style.display = 'none';
      if (read.enabled) {
        applyJournalPermissions(read.perms);
        showJournalTemplates(journalTab, read.templates, read.emptyHint);
      } else {
        showJournalDisabled(journalTab);
        showWbStatus('Journal is disabled for your company. Contact your admin.', 'info');
      }
      journalLoaded = true;
      return read.user;
    } catch (error) {
      console.error('Failed to load journal templates:', error);
      if (current()) showWbStatus('Failed to load templates', 'error');
      return null;
    }
  }

  function clearWbStatus(): void {
    statusIsListNote = false;
    checkStatus.cleared();
    if (journalStatusDiv) journalStatusDiv.style.display = 'none';
  }

  /** No Push warning armed and no error list: both describe what the tab showed before. */
  function clearJournalActionState(): void {
    pushConfirmation.reset();
    hideWbErrors();
  }

  /** The Journal tab emptied: nothing read, picked, armed or shown, and nothing known about its sheets. */
  function resetJournalTab(): void {
    journalGuard.forget();
    journalLoaded = false;
    journalTemplates = [];
    selectedTemplate = null;
    activeJournalSheet = null;
    clearJournalActionState();
    // Sheets known for the previous company or user, and their re-checks, go with it.
    journalEdits.forget();
    clearWbStatus();
    if (journalActionsDiv) journalActionsDiv.style.display = 'none';
    if (templateDropdown) {
      templateDropdown.innerHTML = '<option value="">Select a template...</option>';
      templateDropdown.disabled = true;
    }
  }

  /**
   * Every tab's data dropped and the pane drawn again for whoever is signed in now: company picker,
   * banner, licence and which tabs show. The caller reads the open tab again. Never recalculates: a
   * recalculation now would compute every open workbook's cells as whoever that is.
   */
  async function redrawPane(): Promise<void> {
    cachedFormulas = [];
    formulasLoaded = false;
    aliasesLoaded = false;
    resetJournalTab();
    await showAuthenticatedView();
    // showAuthenticatedView marks the dashboard, which need not be the tab that is open.
    activateNavButton(viewManager.getCurrentView());
  }

  /**
   * Why a journal click did nothing: in the Journal tab's status while it shows with Journal on, else
   * as a notice, so the tab's own "Journal is disabled" stays where it is.
   */
  function journalRefused(message: string): void {
    if (viewManager.getCurrentView() === 'journal' && journalEnabled) {
      showWbStatus(message, 'error');
    } else {
      viewManager.notify(message, 'info');
    }
  }

  function setInsertBtnLabel(text: string) {
    const label = insertBtn?.querySelector('.ms-Button-label');
    if (label) label.textContent = text;
  }

  /**
   * Keep the Insert button honest about what a click will do. Repeat clicks
   * during demos came from a button that promised an insert but silently
   * no-oped, so the caption tracks the sheets that actually exist.
   */
  function refreshInsertBtnLabel(sheets: ResolvedJournalSheet[]) {
    if (sheets.length === 0) {
      setInsertBtnLabel('Insert journal sheet');
    } else if (sheets.some(s => !s.hasData)) {
      setInsertBtnLabel('Go to journal sheet');
    } else {
      setInsertBtnLabel('Insert another journal sheet');
    }
  }

  async function currentWorksheetId(): Promise<string> {
    try {
      return await journalSvc.getActiveWorksheetId();
    } catch {
      return '';
    }
  }

  /**
   * Pick which of a template's sheets the action buttons operate on: whichever
   * one the user is looking at, else the most recently created.
   */
  async function pickActiveJournalSheet(sheets: ResolvedJournalSheet[]): Promise<ResolvedJournalSheet | null> {
    if (sheets.length === 0) return null;
    const activeId = await currentWorksheetId();
    return sheets.find(s => s.worksheetId === activeId) ?? sheets[sheets.length - 1] ?? null;
  }

  async function syncJournalSheetState(): Promise<ResolvedJournalSheet[]> {
    if (!selectedTemplate) {
      await useJournalSheet(null);
      refreshInsertBtnLabel([]);
      return [];
    }
    let sheets: ResolvedJournalSheet[] = [];
    try {
      sheets = await journalSvc.findSheetsForTemplate(selectedTemplate.id);
    } catch (error) {
      console.error('Failed to resolve journal sheets:', error);
    }
    const previous = activeJournalSheet?.worksheetId;
    await useJournalSheet(await pickActiveJournalSheet(sheets));
    if (activeJournalSheet?.worksheetId !== previous) pushConfirmation.reset();
    refreshInsertBtnLabel(sheets);
    return sheets;
  }

  templateDropdown?.addEventListener('change', async () => {
    selectedTemplate = journalTemplates.find(t => t.id === templateDropdown.value) ?? null;
    if (journalActionsDiv) journalActionsDiv.style.display = selectedTemplate ? 'block' : 'none';
    clearJournalActionState();
    applyTemplateApproval();

    await syncJournalSheetState();
    if (activeJournalSheet) {
      showWbStatus(`Using existing sheet "${activeJournalSheet.worksheetName}"`, 'info');
    }
  });

  // Insert Journal Sheet / Go to Journal Sheet
  document.getElementById('journal-insert-btn')?.addEventListener('click', () => journalActions.run(insertJournalSheet, error => {
    console.error('Insert failed:', error);
    showWbStatus('Failed to create journal sheet', 'error');
  }));

  async function insertJournalSheet(): Promise<void> {
    if (!selectedTemplate) return;
    const sheetIdBeforeClick = await currentWorksheetId();

    // Build from the template as the Portal has it now: an admin may have changed its columns
    // since the pane loaded the list.
    const fresh = await journalSvc.fetchTemplate(selectedTemplate.id);
    if (fresh === 'inactive') {
      await journalGuard.templateInactive();
      return;
    }
    if (fresh) {
      selectedTemplate = fresh;
      journalTemplates = journalTemplates.map(t => (t.id === fresh.id ? fresh : t));
    }
    const template = selectedTemplate;

    // Re-resolve on every click: the sheet may have been renamed, deleted or
    // filled in since the template was picked.
    const sheets = await syncJournalSheetState();

    // An untouched sheet is already what the click would produce, so reuse it
    // instead of stacking up duplicates — unless the template changed since it
    // was made. Anything holding data is left alone.
    const untouched = sheets.find(s => !s.hasData);
    const empty = untouched && await journalSvc.headerMatches(untouched, template) ? untouched : undefined;
    if (empty) {
      const wasActive = empty.worksheetId === sheetIdBeforeClick;
      await journalSvc.activateSheet(empty);
      await useJournalSheet(empty);
      showWbStatus(
        wasActive
          ? `You are already on "${empty.worksheetName}" — start entering lines in row ${empty.nextRow}`
          : `Switched to the empty sheet "${empty.worksheetName}" — fill in your journal lines`,
        'info'
      );
      return;
    }

    const created = await journalSvc.insertTemplate(template);
    journalEdits.forget();
    journalEdits.remember(created.manifest);
    await useJournalSheet(created);
    refreshInsertBtnLabel([...sheets, created]);
    hideWbErrors();
    const added = untouched
      ? `The template changed since "${untouched.worksheetName}" was made, so "${created.worksheetName}" was added with its current columns`
      : sheets.length > 0
        ? `Sheet "${created.worksheetName}" added alongside your existing journal — fill in your lines`
        : `Sheet "${created.worksheetName}" created — fill in your journal lines`;
    if (created.listsError) {
      showWbStatus(`${added}. Not all its dropdowns could be added (${created.listsError}); Refresh dropdowns tries again, and Check for errors still checks every value.`, 'error');
    } else {
      showWbStatus(added, 'success');
    }
  }

  /** The sheet a send acts on, with the template its lines are read against. */
  interface SendTarget {
    sheet: ResolvedJournalSheet;
    template: JournalTemplate;
  }

  /**
   * Auth for the journal calls, and the company and user whose dropdown lists the sheets get: those in
   * the token the calls carry, renewed first if it had aged out. False, after the pane has said why,
   * when that is not who the Journal tab was read for.
   */
  async function prepareJournalService(): Promise<boolean> {
    const headers = await getAuthHeaders();
    const sent = await identityOf(headers);
    if (!await journalGuard.sendsAsLoaded(sent)) return false;
    // A workbook belongs to one company, its journal sheets with it.
    const refusal = await workbookPanel.refusal('working on journals');
    if (refusal) {
      journalRefused(refusal);
      return false;
    }
    journalSvc.setAuthHeaders(headers);
    journalSvc.setListOwner(sent.companyId ?? '', sent.userId);
    return true;
  }

  /** Lists errors in the pane and marks their cells, replacing what was there. */
  async function showErrorsOnSheet(sheet: ResolvedJournalSheet, template: JournalTemplate, errors: ValidationError[]): Promise<void> {
    showWbErrors(errors);
    await journalSvc.clearErrors(template, sheet.worksheetId, sheet.headerRow);
    await journalSvc.renderErrors(errors, template, sheet.worksheetId, sheet.headerRow);
  }

  function activeErpConnectionId(): Promise<string | null> {
    return connectionForNewJournal(() => journalPerms, reloadJournalPermissions, () => journalSvc.firstActiveConnectionId());
  }

  function sendPostsAtOnce(sheet: ResolvedJournalSheet): Promise<boolean> {
    return sheetPostsAtOnce(sheet.manifest, instanceId => journalSvc.instanceHasDraftState(instanceId), erpHasDraftState);
  }

  /** What a passing check says the journal is ready for, from the buttons the user has. */
  function readyFor(): string {
    const canSubmit = submitBtn?.style.display !== 'none';
    const canPush = pushBtn?.style.display !== 'none';
    // Both show only on a template that requires review: the preparer sends
    // it for review, and a colleague checking it sends it to the ERP.
    return canSubmit && canPush
      ? 'ready to send for review, or to send to your ERP if a colleague sent it for review'
      : canSubmit ? 'ready to send for review' : 'ready to push';
  }

  const sendFlow = new JournalSendFlow(journalSvc, {
    status: showWbStatus,
    reading: worksheetId => checkStatus.reading(worksheetId),
    passed: (message, worksheetId) => {
      if (checkStatus.changedWhileChecking(worksheetId)) {
        showWbStatus(EDITED_SINCE_CHECK_MESSAGE, 'info');
        return;
      }
      showWbStatus(message, 'success');
      checkStatus.showingPass(worksheetId);
    },
    showErrors: showWbErrors,
    hideErrors: hideWbErrors,
    setSubmitEnabled,
    readyFor,
    templateInactive: () => journalGuard.templateInactive(),
    connectionInactive: () => journalGuard.connectionInactive(),
  }, activeErpConnectionId);

  /** The journal sheet the user is on and its template, or null after saying there is none. */
  async function sendTarget(): Promise<SendTarget | null> {
    const template = selectedTemplate;
    if (!template) return null;
    await syncJournalSheetState();
    const sheet = activeJournalSheet;
    if (!sheet) {
      showWbStatus('Insert a journal sheet first', 'error');
      return null;
    }
    return { sheet, template };
  }

  /**
   * A refused send: its own reasons replace whatever the last check listed, and a refusal that names
   * no lines clears the list, which belongs to that check.
   */
  async function reportRefusal(prefix: string, error: unknown, target: SendTarget | null): Promise<void> {
    showWbStatus(`${prefix}: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
    // Within the action, so the rights are read before the next click is taken.
    if (error instanceof JournalApiError && isRightsRefusal(error.code)) await reloadJournalPermissions();
    const errors = error instanceof JournalApiError ? error.validationErrors ?? [] : [];
    if (errors.length === 0) {
      hideWbErrors();
      return;
    }
    if (target) {
      await showErrorsOnSheet(target.sheet, target.template, errors).catch((markError: unknown) => console.error('Failed to mark refused lines:', markError));
    } else {
      showWbErrors(errors);
    }
  }

  // Refresh dropdowns — the sheet's lists read again from the ERP, for values added there since.
  document.getElementById('journal-refresh-lists-btn')?.addEventListener('click', () => journalActions.run(async () => {
    const target = await sendTarget();
    if (!target) return;
    showWbStatus('Reading the dropdown lists from the ERP...', 'info');
    const sheet = target.sheet;
    const template = target.template;
    const result = await journalSvc.refreshListsFromErp(template, sheet);
    if (result === 'misfit') {
      showWbStatus(sheetMisfitMessage(sheet.worksheetName), 'error');
      return;
    }
    // Marked in the edit queue, so it never interleaves with an edit of the same cells.
    const notes = result.written > 0 ? await journalEdits.inQueue(() => journalSvc.remarkSheet(template, sheet)) : [];
    const type = result.expected === 0 ? 'info' : result.failed === 0 && notes.length === 0 ? 'success' : 'error';
    showWbStatus(listsRefreshedMessage(result, notes, template), type);
  }, error => {
    console.error('Refresh dropdowns failed:', error);
    showWbStatus(`The dropdowns could not be refreshed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
  }));

  // Check for errors
  document.getElementById('journal-validate-btn')?.addEventListener('click', () => journalActions.run(async () => {
    const target = await sendTarget();
    if (!target) return;
    setSubmitEnabled(false);
    const reading = await sendFlow.read(target.sheet, target.template);
    if (reading) await sendFlow.check(target.sheet, target.template, reading);
    await applyOwnJournalReview();
  }, error => {
    console.error('Validate failed:', error);
    showWbStatus(`Validation failed: ${error instanceof Error ? error.message : 'Unknown error'}`, 'error');
  }));

  // Send for review — the hand-off to a colleague with push access. Not the ERP's approval workflow.
  document.getElementById('journal-submit-btn')?.addEventListener('click', () => {
    let target: SendTarget | null = null;
    return journalActions.run(async () => {
      target = await sendTarget();
      if (!target) return;
      const { sheet } = target;
      // The approver sends the version submitted, so it has to be what the sheet reads.
      const submitted = await sendFlow.send(sheet, target.template, async () => {
        const instanceId = sheet.manifest.instanceId;
        if (!instanceId) return false;
        showWbStatus('Sending for review...', 'info');
        const { submittedById } = await journalSvc.submitForApproval(instanceId);
        sheet.manifest = await journalSvc.updateSheetState(sheet.manifest, { submittedBy: submittedById });
        return true;
      });
      if (!submitted) return;
      setSubmitEnabled(false);
      showWbStatus('Sent for review — someone with push access can now send it to your ERP.', 'success');
      await applyOwnJournalReview();
    }, error => {
      console.error('Send for review failed:', error);
      return reportRefusal('Send for review failed', error, target);
    });
  });

  // Push Draft
  document.getElementById('journal-push-btn')?.addEventListener('click', () => {
    let target: SendTarget | null = null;
    return journalActions.runConfirming(pushConfirmation, async press => {
      target = await sendTarget();
      if (!target) return;
      const sending = target;
      const postsAtOnce = await sendPostsAtOnce(sending.sheet);
      // A click too soon after the warning shows it again, since "Checking..." replaced it.
      if (postsAtOnce && pushConfirmation.click(`${sending.template.id}:${sending.sheet.worksheetId}`, press) !== 'confirmed') {
        showWbStatus(POSTS_AT_ONCE_WARNING, 'info');
        return;
      }
      await sendFlow.send(sending.sheet, sending.template, async (versionId) => {
        showWbStatus(postsAtOnce ? 'Posting to ERP...' : 'Pushing draft to ERP...', 'info');
        const job = await journalSvc.pushDraft(versionId);
        const sent = postsAtOnce ? 'Posted to the ERP.' : 'Draft sent to the ERP. Review and post it there.';
        const alreadySent = postsAtOnce ? 'Already posted to the ERP.' : 'Already sent to the ERP as a draft.';
        await reportJournalJob(job, job.alreadyPosted ? alreadyPostedMessage(job) : job.alreadySent ? alreadySent : sent, sending);
      });
    }, error => {
      console.error('Push failed:', error);
      return reportRefusal('Push failed', error, target);
    }, () => showWbStatus(CHECKING_MESSAGE, 'info'));
  });

  /**
   * A job can come back without an HTTP error and still have failed, so the
   * status decides the message, not the response. erpBatchName is what the ERP
   * shows its users; erpBatchId is often an internal key, so only a fallback.
   */
  async function reportJournalJob(job: JournalJob, successMessage: string, target: SendTarget): Promise<void> {
    if (job.status === 'FAILED' || job.status === 'VALIDATION_ERRORS') {
      showWbStatus(job.errorMessage || 'The ERP did not accept the journal.', 'error');
      const errors = job.validationErrors ?? [];
      if (errors.length > 0) {
        await showErrorsOnSheet(target.sheet, target.template, errors).catch((markError: unknown) => console.error('Failed to mark refused lines:', markError));
      } else {
        hideWbErrors();
      }
      return;
    }
    // A posted journal is found in the ERP by its vouchers, as the Portal shows it; a Business Central
    // batch is gone once posted.
    const vouchers = job.status === 'POSTED' ? job.erpResponse?.documentNumbers ?? [] : [];
    const reference = vouchers.length > 0 ? vouchers.join(', ') : job.erpBatchName || job.erpBatchId;
    const warnings = job.erpResponse?.warnings ?? [];
    // Not a success to celebrate: what the ERP posted is not what the sheet holds.
    const changedInErp = job.erpResponse?.postedWithChanges === true;
    showWbStatus(sentMessage(successMessage, reference || undefined, job.erpResponse?.notes ?? [], warnings.length), changedInErp ? 'info' : 'success');
    if (warnings.length > 0) {
      showWbErrors(warnings.map(message => ({ lineNumber: 0, column: '', message, severity: 'warning' as const })));
    } else {
      hideWbErrors();
    }
  }

  // Finalize & post — sends the draft first when this version has none yet, so one click does both.
  document.getElementById('journal-post-btn')?.addEventListener('click', () => {
    let target: SendTarget | null = null;
    return journalActions.run(async () => {
      target = await sendTarget();
      if (!target) return;
      const sending = target;
      // The button follows the company's ERP; a journal made on one that posts when sent has no draft to
      // finalize, and Post to ERP asks before it posts.
      if (await sendPostsAtOnce(sending.sheet)) {
        showWbStatus(FINALIZE_POSTS_AT_ONCE_MESSAGE, 'error');
        return;
      }
      await sendFlow.send(sending.sheet, sending.template, async (versionId) => {
        showWbStatus('Posting journal to ERP...', 'info');
        const { job, sentDraftOnly } = await journalSvc.finalizeAndPost(versionId);
        await reportJournalJob(job, !sentDraftOnly
          ? (job.alreadyPosted ? alreadyPostedMessage(job) : 'Posted to the ERP.')
          : awaitsApprovalInErp(job)
            ? 'Draft sent to the ERP. This journal is approved and posted in the ERP, not from here.'
            : 'Draft sent to the ERP, but not posted.', sending);
      });
    }, error => {
      console.error('Post failed:', error);
      return reportRefusal('Post failed', error, target);
    });
  });

  /**
   * With several journal sheets open, Validate/Push must act on the one the
   * user is looking at rather than whichever was inserted last.
   */
  async function handleWorksheetActivated(event: Excel.WorksheetActivatedEventArgs): Promise<void> {
    if (!selectedTemplate || viewManager.getCurrentView() !== 'journal') return;
    if (activeJournalSheet?.worksheetId === event.worksheetId) return;
    try {
      const sheets = await journalSvc.findSheetsForTemplate(selectedTemplate.id);
      const match = sheets.find(s => s.worksheetId === event.worksheetId);
      // Moving to an unrelated sheet leaves the current target in place.
      if (!match) return;
      await useJournalSheet(match);
      pushConfirmation.reset();
      refreshInsertBtnLabel(sheets);
      showWbStatus(`Working on sheet "${match.worksheetName}"`, 'info');
    } catch (error) {
      console.error('Failed to track worksheet activation:', error);
    }
  }

  Excel.run(async (context) => {
    context.workbook.worksheets.onActivated.add(handleWorksheetActivated);
    await context.sync();
  }).catch((error: unknown) => console.error('Failed to register worksheet listener:', error));

  if (Office.context?.requirements?.isSetSupported('ExcelApi', '1.9')) {
    Excel.run(async (context) => {
      context.workbook.worksheets.onChanged.add(event => {
        if (checkStatus.changed(event)) showWbStatus(EDITED_SINCE_CHECK_MESSAGE, 'info');
        return journalEdits.onEdit(event);
      });
      await context.sync();
    }).catch((error: unknown) => console.error('Failed to register the journal edit listener:', error));
  }

  async function showAuthenticatedView() {
    // Before the first await: callers show the dashboard just before calling
    // this, and a company switch lands here with the previous company's rows.
    resetSessionCard();
    activateNavButton('dashboard');
    const userInfo = await tokenManager.getUserInfo();

    // The ribbon's working controls are useless without a session, and Portal
    // only makes sense for someone who can administer the company.
    const role = userInfo?.permissions?.[0] ?? '';
    void setRibbonState({ signedIn: !!userInfo, isAdmin: role.endsWith('ADMIN') });

    if (userInfo) {
      const userEmailEl = document.getElementById('user-email');
      if (userEmailEl) userEmailEl.textContent = userInfo.email;

      const nav = document.getElementById('main-nav');
      if (nav) nav.style.display = 'flex';

      await showCompanyInfo();

      try {
        const token = await getValidToken();
        const fingerprint = await deviceFingerprintService.generate();
        if (token) {
          // Deliberately after the token above, and awaited. Firing this
          // unawaited a few lines earlier meant two getToken() calls raced on
          // first load; when that resolved to a refresh, the permissions call
          // went out with no usable token, threw, and the catch hid the Journal
          // tab for the whole session. Clicking Refresh fixed it, which is how
          // the race was spotted — by then the token had settled.
          await refreshJournalTabVisibility();
          const authHeaders = { Authorization: `Bearer ${token}`, 'x-device-fingerprint': fingerprint };
          const sandboxRes = await apiService.get<{
            active: boolean;
            isDemoTemplate?: boolean;
            homeCompanyName?: string;
            activeCompanyName?: string;
            activeCompanyShortName?: string | null;
            activeCompanyIndustryType?: string | null;
          }>('/erp-connections/sandbox/status', {
            headers: authHeaders,
          });
          const sandboxData = sandboxRes?.data;
          viewManager.updateSandboxBanner(
            sandboxData?.active ?? false,
            authHeaders,
            {
              isDemoTemplate: sandboxData?.isDemoTemplate ?? false,
              homeCompanyName: sandboxData?.homeCompanyName,
              activeCompanyName: sandboxData?.activeCompanyName,
              activeCompanyShortName: sandboxData?.activeCompanyShortName ?? null,
              activeCompanyIndustryType: sandboxData?.activeCompanyIndustryType ?? null,
            },
          );

          // License reflects the ACTIVE company: a shared demo template needs
          // no subscription, so show "Demo" rather than an "Expired" badge.
          void updateLicenseStatus(sandboxData?.isDemoTemplate ?? false);
          void updateErpStatus(authHeaders, sandboxData?.active ?? false, sandboxData?.isDemoTemplate ?? false);

          // CompanySwitcher renders the persistent context strip (always visible
          // — for single-company users it shows as a static read-only chip;
          // for multi-company users it's an interactive picker). Industry color,
          // shortName badge, and DEMO/CLIENT/YOU type are surfaced inline.
          await companySwitcher.render(authHeaders, userInfo.companyId ?? null);
          void workbookPanel.refresh();

          apiService.post('/formulas/warm-cache', {}, { headers: authHeaders }).catch(() => {});
        } else {
          void updateLicenseStatus(false);
          updateConnectionStatus('Unknown', 'unknown');
        }
      } catch {
        // non-critical — still resolve the license badge from the subscription
        void updateLicenseStatus(false);
        updateConnectionStatus('Unknown', 'unknown');
      }
    }
  }

  /** A recalculation the switch did not start waits for it, as for any pane action. */
  async function switchCompany(target: { id: string; name: string }): Promise<void> {
    return duringPaneAction(() => switchCompanyNow(target));
  }

  async function switchCompanyNow(target: { id: string; name: string }) {
    // A running journal action holds this company's session; the switch would read the Journal tab
    // again for the next one under it.
    if (journalGuard.busy) {
      viewManager.notify(JOURNAL_BUSY_MESSAGE, 'info');
      return;
    }
    // Block ALL UI interaction during the switch. Without this overlay,
    // any company-dependent action fired during the 1-2s window between
    // /switch returning a new JWT and the UI re-rendering would use
    // stale headers / stale company context — backend returns the
    // previous company's data, which then fails (e.g. sample formulas
    // for company A loaded into a sheet, but evaluated against company B).
    // The overlay enforces "you cannot interact during a context change"
    // which is the only safe semantic.
    const switched = journalGuard.switchStarting();
    showSwitchingOverlay(target.name);
    let moved = false;
    try {
      await dropCachedResults();
      const token = await getValidToken();
      if (!token) return;
      const fingerprint = await deviceFingerprintService.generate();

      const switchRes = await apiService.post<{ accessToken: string; refreshToken: string; expiresIn: number }>(
        '/external-access/switch',
        { companyId: target.id },
        { headers: { Authorization: `Bearer ${token}`, 'x-device-fingerprint': fingerprint } },
      );

      const newAccess = switchRes?.data?.accessToken;
      const newRefresh = switchRes?.data?.refreshToken;
      if (!switchRes.success || !newAccess) {
        console.error('Company switch failed:', switchRes.error);
        return;
      }

      const claims = tokenManager.decodeJWT(newAccess) as { email?: string; userId?: string; companyId?: string; role?: string };
      await tokenManager.storeToken(newAccess, {
        email: claims.email ?? '',
        userId: claims.userId ?? '',
        company: target.name,
        companyId: claims.companyId ?? target.id,
        permissions: claims.role ? [claims.role] : [],
      }, newRefresh);
      // A company chosen in this workbook's pane becomes this workbook's, and no other workbook's.
      const company = { companyId: claims.companyId ?? target.id, companyName: target.name };
      moveWorkbookTo(company);
      moveStarted(company);
      moved = true;

      // Again after the swap: a cell may have cached the old company's value
      // while the switch was in flight.
      await dropCachedResults();

      // Reset cached state — every view must refetch against the new company.
      cachedFormulas = [];
      formulasLoaded = false;
      aliasesLoaded = false;
      resetJournalTab();

      const formulasList = document.getElementById('formulas-list');
      if (formulasList) formulasList.innerHTML = '<div class="loading-formulas">Loading formulas...</div>';
      const aliasesList = document.getElementById('aliases-list');
      if (aliasesList) aliasesList.innerHTML = '<div class="loading-formulas">Loading aliases...</div>';

      await showAuthenticatedView();

      // Brief confirmation so the company change is visually unmistakable.
      // Industry/type are surfaced inline in the company switcher chip too,
      // but the toast adds an immediate "something happened" cue.
      viewManager.notify(`Switched to ${target.name}`, 'success');

      // Refresh journal tab visibility — the new company may have a
      // different policy than the previous one.
      await refreshJournalTabVisibility();

      // If the user was on a tab when they switched, refetch that tab's data.
      const activeView = viewManager.getCurrentView();
      if (activeView === 'formulas') {
        await loadFormulas();
      } else if (activeView === 'aliases') {
        await loadAliases();
      } else if (activeView === 'journal') {
        await journalGuard.load();
      }
    } catch (error) {
      console.error('Company switch error:', error);
    } finally {
      // Whatever failed after the move, the workbook now belongs to the new company.
      if (moved) await recalculateMovedWorkbook(target.name);
      hideSwitchingOverlay();
      switched();
    }
  }

  /**
   * Recalculates a workbook just moved to another company, so its cells show that company's figures.
   * Not while a journal action runs, which it would cut across. Either way the user is told how to
   * finish, at once and on the workbook's line until it is done, and a sheet's activation tries again.
   */
  async function recalculateMovedWorkbook(companyName: string): Promise<void> {
    if (journalGuard.busy) {
      moveFailed();
      viewManager.notify(finishMoveMessage(companyName), 'error');
    } else {
      await recalculateThisWorkbook().catch((error: unknown) => {
        console.warn('Recalculation after the company switch failed:', error);
        moveFailed();
        viewManager.notify(finishMoveMessage(companyName), 'error');
      });
    }
    // The workbook's line says whether the move is finished.
    void workbookPanel.refresh();
  }

  let recalcTotalFormulas = 0;

  function updateProgress(label: string, pct: number, status?: string) {
    if (pct < 0) {
      hideProgressUI();
      return;
    }
    showProgressUI({
      loading: pct < 100,
      status: status || 'fetching',
      progress: Math.round(pct),
      message: label,
      totalFormulas: recalcTotalFormulas || undefined,
      completedFormulas: pct >= 100 ? recalcTotalFormulas : undefined,
    });
  }

  /**
   * Recalculate by letting Excel recalculate. The cells resolve their own
   * arguments — references, date cells, cells joined by + — which the formula
   * text alone cannot, so a pass of our own over the text sent requests no cell
   * asked for and the cells then fetched everything again.
   */
  async function batchRecalc(
    scope: 'sheet' | 'workbook',
    options: { forceRefresh?: boolean } = {}
  ): Promise<boolean> {
    if (!(await tokenManager.getUserInfo())) {
      showActionResult(SIGN_IN_FIRST_MESSAGE, 'error');
      return false;
    }
    // In a workbook that belongs to another company, recalculating would only
    // turn its saved figures into errors.
    const refusal = await workbookPanel.refusal('recalculating');
    if (refusal) {
      showActionResult(refusal, 'error');
      return false;
    }
    paneRecalcRunning = true;
    try {
      await duringPaneAction(() => runRecalc(scope, options));
    } finally {
      paneRecalcRunning = false;
    }
    return true;
  }

  async function runRecalc(
    scope: 'sheet' | 'workbook',
    options: { forceRefresh?: boolean }
  ) {
    const startTime = Date.now();
    debugLog(`Bifrost: batchRecalc started, scope=${scope}`);

    // A recalc rewrites the very cells an open hardcode dialog just counted.
    dismissActionPanel();

    try {
      updateProgress('Recalculating…', 5);
      await dropCachedResults();
      if (options.forceRefresh) {
        (globalThis as unknown as { __bifrostForceRefresh?: boolean }).__bifrostForceRefresh = true;
      }

      const bifrostFormulas: Array<{ functionType: string; formulaName: string; params: (string | number)[]; sheetIndex: number; row: number; col: number; formulaText: string }> = [];
      let sheetIds: string[] = [];
      let manual = false;
      await Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets;
        worksheets.load('items');
        const calculatesManually = manualCalculationOf(context);
        await context.sync();
        const sheets = scope === 'sheet' ? [worksheets.getActiveWorksheet().load('id')] : worksheets.items;
        const ranges = sheets.map(ws => ws.getUsedRangeOrNullObject().load(['formulas', 'values', 'rowIndex', 'columnIndex']));
        await context.sync();

        ranges.forEach((range, si) => {
          if (range.isNullObject) return;
          const before = bifrostFormulas.length;
          collectFormulasWithPositions(range.formulas, si, bifrostFormulas);
          // A workbook made outside Excel binds the add-in's function names only
          // when a formula is entered, so its cells read #NAME? until then.
          for (const f of bifrostFormulas.slice(before)) {
            if (range.values[f.row]?.[f.col] === '#NAME?') {
              sheets[si].getCell(range.rowIndex + f.row, range.columnIndex + f.col).formulas = [[f.formulaText]];
            }
          }
        });

        if (bifrostFormulas.length > 0) {
          calculateSheets(sheets);
        }
        await context.sync();
        sheetIds = sheets.map(ws => ws.id);
        manual = calculatesManually();
      });
      // A cell this set off may bind the workbook; recalculating it again after that is then not needed.
      if (bifrostFormulas.length > 0) {
        if (scope === 'workbook') everyCellSetOff();
        else sheetCellsSetOff(sheetIds);
      }

      if (bifrostFormulas.length === 0) {
        updateProgress('', -1);
        showActionResult(`No ${ADDIN_NAMESPACE} formulas found on the ${scope}.`);
        return;
      }

      recalcTotalFormulas = bifrostFormulas.length;
      let shown = 10;
      const settled = await waitForCells((done, total) => {
        // Only forward: a new wave adds to the total, it must not pull the bar back.
        shown = Math.max(shown, total > 0 ? 10 + Math.round((85 * done) / total) : 10);
        updateProgress(total > 0 ? `Fetching from the ERP… ${done} of ${total} request(s)` : 'Recalculating…', shown);
      });
      const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
      showProgressComplete();
      // In manual calculation, what reads the cells waits for F9 in Excel.
      showActionResult(withManualHint(settled
        ? `Updated ${bifrostFormulas.length} formula(s) on the ${scope} in ${elapsed}s.`
        : `Still fetching after ${elapsed}s. Cells fill in as the ERP answers.`, manual));
    } catch (error) {
      console.error('Batch recalc error:', error);
      updateProgress('', -1);
      showActionResult('Calculation failed. Check the connection status and try again.', 'error');
    }
  }

  function collectFormulasWithPositions(
    formulas: (string | number | boolean)[][],
    sheetIndex: number,
    out: Array<{ functionType: string; formulaName: string; params: (string | number)[]; sheetIndex: number; row: number; col: number; formulaText: string }>
  ) {
    for (let r = 0; r < formulas.length; r++) {
      for (let c = 0; c < formulas[r].length; c++) {
        const f = formulas[r][c];
        if (typeof f === 'string' && f.startsWith('=')) {
          const parsed = parseBifrostFormula(f);
          if (parsed) out.push({ ...parsed, sheetIndex, row: r, col: c, formulaText: f });
        }
      }
    }
  }

  function setupDashboardActions() {
    document.getElementById('action-recalc-sheet')?.addEventListener('click', () => batchRecalc('sheet'));

    document.getElementById('action-recalc-workbook')?.addEventListener('click', () => batchRecalc('workbook'));

    document.getElementById('action-hardcode')?.addEventListener('click', () => showHardcodeDialog());

    // Prepare for Claude — writes this company's vocabulary into a sheet.
    // Shown to everyone rather than gated on a seat, matching how the portal
    // treats the add-on: the capability is worth knowing about and the blocker
    // is one the customer can lift. The backend refuses without a seat and its
    // message says exactly who to ask.
    const prepareClaudeBtn = document.getElementById('action-prepare-claude') as HTMLButtonElement | null;
    prepareClaudeBtn?.addEventListener('click', () => duringPaneAction(async () => {
      // The sheet carries the current company's vocabulary, which is no use in another company's workbook.
      const refusal = await workbookPanel.refusal('preparing it for Claude');
      if (refusal) {
        showActionResult(refusal, 'error');
        return;
      }
      const label = prepareClaudeBtn.querySelector('.action-label');
      const originalText = label?.textContent ?? 'Prepare for Claude';
      prepareClaudeBtn.disabled = true;
      if (label) label.textContent = 'Preparing…';
      try {
        const { prepareVocabularySheet } = await import('../services/ClaudeVocabularyService');
        const result = await prepareVocabularySheet(await getAuthHeaders());
        showActionResult(
          `Wrote ${result.formulaCount} formula(s) to the "${result.sheetName}" sheet. Claude can read it from here.`
        );
      } catch (error) {
        console.error('Prepare for Claude error:', error);
        showActionResult(
          error instanceof Error ? error.message : 'Could not prepare the Claude vocabulary sheet',
          'error'
        );
      } finally {
        prepareClaudeBtn.disabled = false;
        if (label) label.textContent = originalText;
        await refreshClaudeSheetLink();
      }
    }));

    // Remove Claude Sheet — the vocabulary sheet is a demo device, and it
    // carries the chart of accounts into a file that leaves our control. Taking
    // it back out should not require the user to know which tab was ours.
    // Offered as a secondary link, and only while the sheet actually exists:
    // as a permanent tile it was a delete button competing for attention with
    // the actions people use every day.
    document.getElementById('action-remove-claude-sheet')?.addEventListener('click', async () => {
      try {
        const { removeVocabularySheet } = await import('../services/ClaudeVocabularyService');
        const removed = await removeVocabularySheet();
        showActionResult(
          removed ? 'Removed the Bifrost Vocabulary sheet' : 'No Bifrost Vocabulary sheet in this workbook'
        );
      } catch (error) {
        console.error('Remove Claude sheet error:', error);
        showActionResult(
          error instanceof Error ? error.message : 'Could not remove the Bifrost Vocabulary sheet',
          'error'
        );
      } finally {
        await refreshClaudeSheetLink();
      }
    });

    void refreshClaudeSheetLink();

    document.getElementById('status-clear-cache')?.addEventListener('click', async () => {
      try {
        await dropCachedResults();
        accessibilityManager.announceSuccess('Cache cleared');
        showActionResult('Cleared the local formula cache. The next calculation goes back to the server.');
      } catch (error) {
        console.error('Clear cache error:', error);
        accessibilityManager.announceError('Failed to clear cache');
        showActionResult('Could not clear the cache', 'error');
      }
    });
  }

  async function isAdminUser(): Promise<boolean> {
    const userInfo = await tokenManager.getUserInfo();
    return (userInfo?.permissions?.[0] ?? '').endsWith('ADMIN');
  }

  async function showCompanyInfo() {
    const companyName = document.getElementById('company-name');
    const companyRole = document.getElementById('company-role');

    try {
      const userInfo = await tokenManager.getUserInfo();
      if (companyName) {
        companyName.textContent = userInfo?.company || 'Unknown Company';
        // The company switcher chip above already names the active company.
        // Only fall back to printing it here if that chip isn't rendered.
        companyName.style.display = document.getElementById('company-switcher') ? 'none' : 'block';
      }
      if (companyRole) {
        // The role is stored in permissions[0] by every path that builds a
        // UserInfo (password login, SSO sync, company switch). UserInfo has no
        // `role` field — reading one behind a cast silenced the type error and
        // made this line print "Role: USER" for everyone, admins included.
        companyRole.textContent = roleLabel(userInfo?.permissions?.[0]);
      }
    } catch (error) {
      console.error('Failed to show company info:', error);
      if (companyName) {
        companyName.textContent = 'Unknown Company';
        companyName.style.display = 'block';
      }
    }
  }

  async function loadFormulas() {
    const formulasList = document.getElementById('formulas-list');
    const searchContainer = document.getElementById('formulas-search-container');
    if (!formulasList) return;

    try {
      const token = await getValidToken();
      if (!token) {
        formulasList.innerHTML = '<p class="error-text">Not authenticated</p>';
        return;
      }

      formulasList.innerHTML = '<div class="loading-formulas">Loading formulas...</div>';

      const fingerprint = await deviceFingerprintService.generate();
      const response = await apiService.get<{ companies: CompanyWithFormulas[] }>('/formulas/all-companies', {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-device-fingerprint': fingerprint
        }
      });

      if (response.success) {
        const companies: CompanyWithFormulas[] = response.data?.companies || [];
        const activeId = activeCompanyId(companies, (await tokenManager.getUserInfo())?.companyId);
        for (const company of companies) company.isActive = company.companyId === activeId;

        // Flatten for cachedFormulas (used by FormulaBuilder click handlers)
        cachedFormulas = [];
        for (const company of companies) {
          for (const f of company.formulas) {
            cachedFormulas.push({
              ...f,
              companyShortName: company.shortName,
              companyName: company.companyName,
              companyType: company.type,
              companyId: company.companyId,
              companyIsActive: company.isActive === true,
            } as Formula & { companyShortName: string | null; companyName: string; companyType: string; companyId: string; companyIsActive: boolean });
          }
        }
        formulasLoaded = true;

        const totalFormulas = companies.reduce((sum, c) => sum + c.formulas.length, 0);
        if (totalFormulas === 0) {
          const hint = await isAdminUser()
            ? 'Create them in the web portal under Ledger.'
            : 'Ask your admin to create them in the web portal under Ledger.';
          formulasList.innerHTML = `
            <p class="no-formulas">No formulas configured.</p>
            <p class="hint-text">${hint}</p>
          `;
          if (searchContainer) searchContainer.style.display = 'none';
          return;
        }

        if (searchContainer) searchContainer.style.display = 'block';
        renderGroupedFormulas(companies);
        setupFormulaSearch();
      } else {
        console.warn('Formulas API error:', response.error);
        formulasList.innerHTML = `
          <p class="no-formulas">Could not load formulas.</p>
          <p class="hint-text">${response.error || 'Please try again later.'}</p>
        `;
      }
    } catch (error) {
      console.error('Failed to load formulas:', error);
      formulasList.innerHTML = `
        <p class="no-formulas">Could not load formulas.</p>
        <p class="hint-text">Check your connection and try again.</p>
      `;
    }
  }

  function getFormulaDisplayName(formula: Formula, company: CompanyWithFormulas): string {
    return `${getFunctionName(formula.functionType)}("${formulaCallName(formula.name, groupCompany(company))}", ...)`;
  }

  function groupCompany(company: CompanyWithFormulas): FormulaCompany {
    return { companyId: company.companyId, shortName: company.shortName, isActive: company.isActive === true };
  }

  /**
   * Build a paste-ready formula string for the Copy button. Uses the formula's
   * parameter names as placeholders so the user can replace them inline.
   * Returns e.g. `=BIFROST.LEDGER("NAG:BALANCE",year,month,account)`. No spaces
   * after the separators — Excel preserves them and they follow the formula into
   * every cell it is dragged to.
   */
  function buildCopyFormula(formula: Formula, company: CompanyWithFormulas): string {
    const fnName = getFunctionName(formula.functionType);
    const formulaName = formulaCallName(formula.name, groupCompany(company));
    const paramList = (formula.parameters || []).join(',');
    return paramList
      ? `=${fnName}("${formulaName}",${paramList})`
      : `=${fnName}("${formulaName}")`;
  }

  /**
   * Copy text to clipboard with a fallback for older Excel runtimes that lack
   * navigator.clipboard. The Excel taskpane sandbox supports both paths.
   */
  async function copyToClipboard(text: string): Promise<boolean> {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // fall through to execCommand
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }

  /**
   * Company groups start collapsed so a tenant with several companies and
   * dozens of formulas opens to a scannable list of headers instead of a wall
   * of cards. A group the user opened stays open on the next load — stored per
   * company id. localStorage, not roamingSettings: TokenManager documents that
   * roamingSettings does not sync reliably in this add-in, so state is
   * per-device.
   */
  const FORMULA_GROUP_STATE_KEY = 'bifrost-formula-groups-expanded';

  /** Explicit open/closed per company; a company with no entry opens only if it is the active one. */
  function loadGroupExpansion(): Record<string, boolean> {
    try {
      const raw = localStorage.getItem(FORMULA_GROUP_STATE_KEY);
      if (!raw) return {};
      const parsed: unknown = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return {};
      return parsed as Record<string, boolean>;
    } catch {
      return {};
    }
  }

  function saveGroupExpansion(companyId: string, expanded: boolean): void {
    try {
      const state = loadGroupExpansion();
      state[companyId] = expanded;
      localStorage.setItem(FORMULA_GROUP_STATE_KEY, JSON.stringify(state));
    } catch {
      // Storage unavailable (private mode / quota) — collapse state is
      // cosmetic, so fall back to in-session behaviour only.
    }
  }

  function setGroupExpanded(group: HTMLElement, expanded: boolean): void {
    const header = group.querySelector('.formula-company-header') as HTMLElement | null;
    const body = group.querySelector('.formula-company-body') as HTMLElement | null;
    const icon = group.querySelector('.company-collapse-icon') as HTMLElement | null;
    if (!header || !body || !icon) return;
    body.style.display = expanded ? '' : 'none';
    header.setAttribute('aria-expanded', String(expanded));
  }

  function renderGroupedFormulas(companies: CompanyWithFormulas[]) {
    const formulasList = document.getElementById('formulas-list');
    if (!formulasList) return;

    // Order: the active company first, then primary (your home) → external
    // (granted) → demo (templates).
    const typeRank = { primary: 0, external: 1, demo: 2 } as const;
    const sorted = [...companies].sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      const aRank = typeRank[a.type as keyof typeof typeRank] ?? 99;
      const bRank = typeRank[b.type as keyof typeof typeRank] ?? 99;
      if (aRank !== bRank) return aRank - bRank;
      return a.companyName.localeCompare(b.companyName);
    });

    let html = '';
    const expansionState = loadGroupExpansion();

    for (const company of sorted) {
      if (company.formulas.length === 0) continue;

      const expanded = expansionState[company.companyId] ?? company.isActive === true;

      const isPrimary = company.type === 'primary';
      const prefixed = needsPrefix(groupCompany(company));
      const isDemo = company.type === 'demo';
      const badgeClass = isPrimary
        ? 'company-badge-primary'
        : isDemo
          ? 'company-badge-demo'
          : 'company-badge-external';
      const badgeText = company.shortName || (isPrimary ? 'You' : '?');
      const noShortName = isUnreachable(groupCompany(company));
      const industry = company.industryType || 'unknown';
      const groupClasses = `formula-company-group industry-${industry} formula-company-group--type-${company.type}`;
      const industryLabel = isDemo && company.industryType
        ? ` · ${company.industryType.charAt(0).toUpperCase() + company.industryType.slice(1)} demo`
        : '';

      html += `<div class="${groupClasses}" data-company-id="${company.companyId}">`;
      html += `<div class="formula-company-header ${badgeClass}" role="button" tabindex="0" aria-expanded="${expanded}" style="cursor:pointer;">`;
      html += `  <span class="company-badge ${badgeClass}">${escapeHtml(badgeText)}</span>`;
      html += `  <span class="company-header-name">${escapeHtml(company.companyName)}${escapeHtml(industryLabel)}</span>`;
      if (company.isActive) html += `  <span class="company-active-tag" title="The company you are working in: its formulas need no prefix">Active</span>`;
      html += `  <span class="company-formula-count">${company.formulas.length}</span>`;
      html += `  <span class="company-collapse-icon">${chevronRightIcon}</span>`;
      html += `</div>`;
      html += `<div class="formula-company-body"${expanded ? '' : ' style="display:none;"'}>`;

      if (noShortName) {
        html += `<div class="company-no-shortname-warning">`;
        html += `  Short name not set — switch to ${escapeHtml(company.companyName)} to use these formulas, or ask an admin to set a short name in the web portal.`;
        html += `</div>`;
      } else if (prefixed && company.shortName) {
        html += `<div class="company-prefix-hint">`;
        html += `  Use prefix <code>${escapeHtml(company.shortName)}:</code> before formula name`;
        html += `  <details class="company-prefix-explainer">`;
        html += `    <summary>Why prefix?</summary>`;
        html += `    <p>The prefix lets you query <strong>${escapeHtml(company.companyName)}</strong> from inside any other company without leaving your current context. To make ${escapeHtml(company.companyName)} your active company instead, use the company switcher above.</p>`;
        html += `  </details>`;
        html += `</div>`;
      }

      for (const formula of company.formulas) {
        const fnPreview = getFormulaDisplayName(formula, company);
        const copyFormula = buildCopyFormula(formula, company);
        const typeBadge = functionTypeLabel(formula.functionType || 'ledger');
        const disabled = noShortName ? ' formula-card-disabled' : '';
        const multiCompanyHint = formula.multiCompany
          ? `<span class="formula-multi-company">${arrowRightIcon} ${escapeHtml(formula.sourceCompanyName || 'multi-company')}</span>`
          : '';
        const referenceUrl = formulaReferenceUrl(formula);
        const referenceLink = referenceUrl
          ? `<button class="formula-card-reference" data-reference-url="${escapeHtml(referenceUrl)}" title="How ${escapeHtml(formula.name)} is calculated">How it works</button>`
          : '';
        const demoHint = formula.erpType === 'DEMO'
          ? `<span class="formula-demo-badge" title="Runs against sample data, not your ERP">Demo</span>`
          : '';

        html += `
          <div class="formula-card${disabled}" data-formula-id="${formula.id}" tabindex="0" role="button" aria-label="Build ${escapeHtml(formula.displayName || formula.name)} formula">
            <div class="formula-card-header">
              <span class="formula-type-badge badge-${formula.functionType}">${typeBadge}</span>
              <span class="formula-card-name">${escapeHtml(formula.displayName || formula.name)}</span>
              ${demoHint}
              ${multiCompanyHint}
              <button class="formula-card-copy" data-copy-text="${escapeHtml(copyFormula)}" aria-label="Copy ${escapeHtml(formula.displayName || formula.name)} formula to clipboard" title="Copy formula to clipboard"${noShortName ? ' disabled' : ''}>Copy</button>
            </div>
            <code class="formula-card-fn">${escapeHtml(fnPreview)}</code>
            ${formula.description ? `<p class="formula-card-desc">${escapeHtml(formula.description)}</p>` : ''}
            ${referenceLink}
          </div>
        `;
      }

      html += `</div>`; // formula-company-body
      html += `</div>`; // formula-company-group
    }

    formulasList.innerHTML = html;

    // Attach collapse toggle handlers
    formulasList.querySelectorAll('.formula-company-header').forEach(header => {
      const toggle = () => {
        const group = header.parentElement as HTMLElement | null;
        if (!group) return;
        const expanded = header.getAttribute('aria-expanded') !== 'true';
        setGroupExpanded(group, expanded);
        const companyId = group.dataset.companyId;
        if (companyId) saveGroupExpansion(companyId, expanded);
      };
      header.addEventListener('click', toggle);
      header.addEventListener('keydown', (e) => {
        if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
          e.preventDefault();
          toggle();
        }
      });
    });

    // Attach click handlers (skip disabled cards)
    formulasList.querySelectorAll('.formula-card:not(.formula-card-disabled)').forEach(card => {
      const handler = () => {
        const id = (card as HTMLElement).dataset.formulaId;
        const formula = cachedFormulas.find(f => f.id === id);
        if (formula && formulaBuilder) {
          if (browseContainer) browseContainer.style.display = 'none';
          if (buildContainer) buildContainer.style.display = 'block';
          formulaBuilder.show(formula);
        }
      };
      card.addEventListener('click', (e) => {
        // Ignore clicks that originated on the Copy button (it stops
        // propagation itself, but be defensive in case of keyboard focus).
        if ((e.target as HTMLElement).closest('.formula-card-copy, .formula-card-reference')) return;
        handler();
      });
      card.addEventListener('keydown', (e) => {
        // Enter on a button inside the card is that button's click, not the card's.
        if ((e.target as HTMLElement).closest('.formula-card-copy, .formula-card-reference')) return;
        if ((e as KeyboardEvent).key === 'Enter' || (e as KeyboardEvent).key === ' ') {
          e.preventDefault();
          handler();
        }
      });
    });

    // Copy-to-clipboard handlers. Live as their own click target separate from
    // the card so users can grab the example formula without opening the
    // FormulaBuilder. Visual feedback via brief notification.
    formulasList.querySelectorAll<HTMLButtonElement>('.formula-card-reference').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        openFormulaReference(btn.dataset.referenceUrl || '');
      });
    });

    formulasList.querySelectorAll<HTMLButtonElement>('.formula-card-copy').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (btn.disabled) return;
        const text = btn.dataset.copyText || '';
        const ok = await copyToClipboard(text);
        viewManager.notify(
          ok ? 'Formula copied — paste into a cell' : 'Could not copy to clipboard',
          ok ? 'success' : 'error',
        );
      });
    });
  }

  /**
   * Filter the rendered cards. Groups are collapsed by default, so a search has
   * to open the ones that contain hits — otherwise the matches stay hidden
   * inside a closed body. Clearing the query restores each group's saved state.
   */
  function applyFormulaFilter(rawQuery: string) {
    const query = rawQuery.toLowerCase().trim();
    const groups = document.querySelectorAll<HTMLElement>('.formula-company-group');
    const expansionState = loadGroupExpansion();

    groups.forEach(group => {
      const cards = group.querySelectorAll('.formula-card');
      let visibleCount = 0;

      cards.forEach(card => {
        const name = card.querySelector('.formula-card-name')?.textContent?.toLowerCase() || '';
        const fn = card.querySelector('.formula-card-fn')?.textContent?.toLowerCase() || '';
        const desc = card.querySelector('.formula-card-desc')?.textContent?.toLowerCase() || '';
        const matches = !query || name.includes(query) || fn.includes(query) || desc.includes(query);
        (card as HTMLElement).style.display = matches ? '' : 'none';
        if (matches) visibleCount++;
      });

      group.style.display = visibleCount > 0 || !query ? '' : 'none';
      setGroupExpanded(group, query ? visibleCount > 0 : expansionState[group.dataset.companyId || ''] === true);
    });
  }

  function setupFormulaSearch() {
    const searchInput = document.getElementById('formulas-search') as HTMLInputElement;
    if (!searchInput) return;

    // Re-apply a query left over from before a reload (e.g. after switching
    // company), so the list the user sees still matches what they typed.
    if (searchInput.value) applyFormulaFilter(searchInput.value);

    if (formulaSearchWired) return;
    formulaSearchWired = true;
    searchInput.addEventListener('input', () => applyFormulaFilter(searchInput.value));
  }

  /**
   * Bifrost Entries panel.
   *
   * Describes the current selection rather than a loaded list, so there is no
   * `entriesLoaded` flag: it re-reads on every visit and on demand.
   */
  let selectedEntriesFormula: SelectedFormula | null = null;

  function setEntriesResult(message: string, isError = false): void {
    const el = document.getElementById('entries-result');
    if (!el) return;
    el.textContent = message;
    el.className = isError ? 'entries-result entries-result--error' : 'entries-result';
    el.style.display = message ? 'block' : 'none';
  }

  /**
   * The formulas this company can drill into, rendered as ready-to-insert
   * _ENTRIES calls. Only the active company's: a drilldown resolves the name
   * in the session's company and takes no company prefix, so another
   * company's formula here would drill into the active one's of that name. This is the tab's resting state: the drilldown above only
   * appears when a Bifrost cell happens to be selected, which left the tab
   * looking empty most of the time and disagreeing with the Portal.
   */
  async function renderEntriesCatalogue(): Promise<void> {
    const list = document.getElementById('entries-list');
    if (!list) return;

    if (cachedFormulas.length === 0) {
      // Shares the Ledger tab's fetch rather than adding a second one; it
      // populates cachedFormulas, which is what the builder uses too.
      await loadFormulas();
    }

    const drillable = cachedFormulas.filter(f => f.companyIsActive !== false);
    if (drillable.length === 0) {
      list.innerHTML = '<p class="entries-empty">No formulas are defined for this company yet.</p>';
      return;
    }

    list.innerHTML = drillable
      .map((f, i) => {
        const params = (f.parametersSchema?.parameters ?? []).map(p => p.name);
        const call = `=${ADDIN_NAMESPACE}.ENTRIES("${f.name}"${params.map(p => `, ${p}`).join('')})`;
        return `<button type="button" class="entries-item" data-entries-index="${i}">
            <span class="entries-item-name">${escapeHtml(f.displayName || f.name)}</span>
            <code class="entries-item-call">${escapeHtml(call)}</code>
          </button>`;
      })
      .join('');

    for (const el of list.querySelectorAll<HTMLElement>('[data-entries-index]')) {
      el.addEventListener('click', () => {
        const formula = drillable[Number(el.dataset.entriesIndex)];
        if (formula) openEntriesBuilder(formula);
      });
    }
  }

  function openEntriesBuilder(formula: Formula): void {
    if (!entriesBuilder || !entriesBuildContainer) return;
    setEntriesResult('');
    const catalogue = document.getElementById('entries-catalogue');
    if (catalogue) catalogue.style.display = 'none';
    entriesBuildContainer.style.display = 'block';
    entriesBuilder.show(formula);
  }

  async function refreshEntriesPanel(): Promise<void> {
    // Re-reading the selection starts over from the catalogue.
    closeEntriesBuilder();
    const catalogue = document.getElementById('entries-catalogue');
    const hint = document.getElementById('entries-catalogue-hint');
    const detail = document.getElementById('entries-detail');
    if (!catalogue || !detail) return;

    setEntriesResult('');
    try {
      selectedEntriesFormula = await readSelectedFormula();
    } catch (error) {
      debugLog('Entries: could not read the selection', error);
      selectedEntriesFormula = null;
    }

    // The catalogue is always available; the wording just changes depending on
    // whether the drilldown above it has something to show.
    catalogue.style.display = 'block';
    if (hint) {
      hint.textContent = selectedEntriesFormula
        ? 'Or insert an Entries formula directly:'
        : 'Insert an Entries formula:';
    }
    void renderEntriesCatalogue();

    if (!selectedEntriesFormula) {
      detail.style.display = 'none';
      return;
    }

    detail.style.display = 'block';

    const address = document.getElementById('entries-source-address');
    const source = document.getElementById('entries-source-formula');
    const target = document.getElementById('entries-target-formula');
    if (address) address.textContent = selectedEntriesFormula.address;
    if (source) source.textContent = selectedEntriesFormula.formulaText;
    if (target) target.textContent = selectedEntriesFormula.entriesFormula;
  }

  async function insertSelectedEntries(): Promise<void> {
    if (!selectedEntriesFormula) return;
    const button = document.getElementById('entries-insert-btn') as HTMLButtonElement | null;
    const select = document.getElementById('entries-target') as HTMLSelectElement | null;
    const target = (select?.value || 'below') as InsertTarget;

    if (button) button.disabled = true;
    setEntriesResult('');
    try {
      const placed = await insertEntriesFormula(selectedEntriesFormula, target);
      setEntriesResult(`Entries written to ${placed.sheetName}!${placed.address.split('!').pop()}`);
      accessibilityManager.announce('Entries formula inserted');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      debugLog('Entries: insert failed', error);
      setEntriesResult(`Could not insert the entries: ${message}`, true);
    } finally {
      if (button) button.disabled = false;
    }
  }

  document.getElementById('entries-refresh-btn')?.addEventListener('click', () => {
    void refreshEntriesPanel();
  });
  document.getElementById('entries-insert-btn')?.addEventListener('click', () => {
    void insertSelectedEntries();
  });

  let aliasesLoaded = false;

  async function loadAliases() {
    const aliasesList = document.getElementById('aliases-list');
    const searchContainer = document.getElementById('aliases-search-container');
    if (!aliasesList) return;

    try {
      const token = await getValidToken();
      if (!token) {
        aliasesList.innerHTML = '<p class="error-text">Not authenticated</p>';
        return;
      }

      aliasesList.innerHTML = '<div class="loading-formulas">Loading aliases...</div>';

      const fingerprint = await deviceFingerprintService.generate();
      const response = await apiService.get<AliasData[]>('/aliases', {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-device-fingerprint': fingerprint
        }
      });

      if (response.success && response.data) {
        const aliases: AliasData[] = response.data;

        if (aliases.length === 0) {
          aliasesList.innerHTML = `
            <p class="no-formulas">No aliases configured yet.</p>
            <p class="hint-text">Ask your administrator to create aliases in the web portal.</p>
          `;
          if (searchContainer) searchContainer.style.display = 'none';
          return;
        }

        if (searchContainer) searchContainer.style.display = 'block';

        renderAliases(aliases);

        const searchInput = document.getElementById('aliases-search') as HTMLInputElement;
        searchInput?.addEventListener('input', () => {
          const query = searchInput.value.toLowerCase();
          const filtered = aliases.filter(a =>
            a.alias.toLowerCase().includes(query) || a.erpCode.toLowerCase().includes(query)
          );
          renderAliases(filtered);
        });

        aliasesLoaded = true;
      } else {
        aliasesList.innerHTML = `
          <p class="no-formulas">Could not load aliases.</p>
          <p class="hint-text">${response.error || 'Please try again later.'}</p>
        `;
      }
    } catch (error) {
      console.error('Failed to load aliases:', error);
      aliasesList.innerHTML = `
        <p class="no-formulas">Could not load aliases.</p>
        <p class="hint-text">Check your connection and try again.</p>
      `;
    }
  }

  function renderAliases(aliases: AliasData[]) {
    const aliasesList = document.getElementById('aliases-list');
    if (!aliasesList) return;

    if (aliases.length === 0) {
      aliasesList.innerHTML = '<p class="no-formulas">No matching aliases.</p>';
      return;
    }

    const items = aliases.map(a => `
      <div class="alias-item">
        <span class="alias-name">${escapeHtml(a.alias)}</span>
        <span class="alias-arrow">${arrowRightIcon}</span>
        <span class="alias-code">${escapeHtml(a.erpCode)}</span>
      </div>
    `).join('');

    aliasesList.innerHTML = items;
  }

  const refreshAliasesBtn = document.getElementById('refresh-aliases-btn');
  refreshAliasesBtn?.addEventListener('click', async () => {
    aliasesLoaded = false;
    await loadAliases();
  });

  function showLoginView() {
    const nav = document.getElementById('main-nav');
    if (nav) nav.style.display = 'none';

    void setRibbonState({ signedIn: false, isAdmin: false });

    // Tear down anything that might leak company info into the login screen.
    companySwitcher.clear();
    workbookPanel.clear();
    viewManager.updateSandboxBanner(false);
    resetSessionCard();

    // Drop cached company-scoped state so a re-login does not show the
    // previous user's data while the new fetch is in flight.
    void dropCachedResults();
    cachedFormulas = [];
    formulasLoaded = false;
    aliasesLoaded = false;
    resetJournalTab();

    emailInput.value = '';
    passwordInput.value = '';
    hideError();
  }

  function activateNavButton(view: string) {
    const navButtons = document.querySelectorAll('.nav-btn');
    navButtons.forEach(btn => {
      const isMatch = btn.getAttribute('data-view') === view;
      btn.classList.toggle('active', isMatch);
      btn.setAttribute('aria-selected', isMatch ? 'true' : 'false');
    });
  }

  function setupNavigation(viewManager: ViewManager) {
    const navButtons = document.querySelectorAll('.nav-btn');

    const handleViewSwitch = performanceManager.throttle((view: string, button: Element) => {
      performanceManager.measure(`view-switch-${view}`, () => {
        navButtons.forEach(btn => {
          btn.classList.remove('active');
          btn.setAttribute('aria-selected', 'false');
        });
        button.classList.add('active');
        button.setAttribute('aria-selected', 'true');

        accessibilityManager.announce(`Switched to ${view} view`);

        performanceManager.requestAnimationFrame(() => {
          viewManager.showView(view as Parameters<typeof viewManager.showView>[0]);
        });

        if (view === 'aliases' && !aliasesLoaded) {
          loadAliases();
        }

        if (view === 'formulas' && !formulasLoaded) {
          loadFormulas();
        }

        if (view === 'journal' && !journalLoaded) {
          void journalGuard.load();
        }

        // Always re-read: unlike the other views this one describes the
        // current selection, which has usually moved since it was last open.
        if (view === 'entries') {
          void refreshEntriesPanel();
        }
      });
    }, 100);

    navButtons.forEach((btn) => {
      const button = btn as HTMLElement;
      button.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const view = target.getAttribute('data-view') || target.closest('[data-view]')?.getAttribute('data-view');
        if (view) {
          // Navigating away abandons an open dialog, so don't leave it armed
          // and waiting behind a hidden tab.
          if (view !== 'dashboard') dismissActionPanel();
          handleViewSwitch(view, btn);
        }
      });

      button.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          const buttons = Array.from(navButtons) as HTMLElement[];
          const currentIndex = buttons.indexOf(button);
          const nextIndex = e.key === 'ArrowRight'
            ? (currentIndex + 1) % buttons.length
            : (currentIndex - 1 + buttons.length) % buttons.length;
          buttons[nextIndex].focus();
        }
      });
    });

    const nav = document.getElementById('main-nav');
    if (nav) nav.style.display = 'none';
  }

  function escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function showError(message: string) {
    if (loginError) {
      loginError.textContent = message;
      loginError.style.display = 'block';
      loginError.setAttribute('role', 'alert');
    }
    accessibilityManager.announceError(message);
  }

  function hideError() {
    if (loginError) loginError.style.display = 'none';
  }

  function showLoading(show: boolean) {
    if (loginLoading) loginLoading.style.display = show ? 'block' : 'none';
    loginBtn.disabled = show;
  }

  function updateConnectionStatus(text: string, state: 'connected' | 'disconnected' | 'unknown') {
    const statusEl = document.getElementById('connection-status');
    if (statusEl) {
      statusEl.textContent = text;
      statusEl.className = state === 'unknown' ? '' : `status-${state}`;
    }
  }

  /**
   * The ERP row mirrors what the backend will run formulas against: an ACTIVE
   * DEMO row is a demo session and wins, otherwise any ACTIVE connection.
   * Without one every cell answers "No ERP connected", so the row must too.
   */
  async function updateErpStatus(authHeaders: Record<string, string>, demoSession: boolean, isDemoTemplate: boolean) {
    if (demoSession || isDemoTemplate) {
      updateConnectionStatus('Demo', 'connected');
      companySwitcher.setErp('Demo');
      return;
    }
    try {
      const res = await apiService.get<{ erpType: string; status: string }[]>('/erp-connections', { headers: authHeaders });
      if (!res.success || !Array.isArray(res.data)) {
        updateConnectionStatus('Unknown', 'unknown');
        return;
      }
      const active = res.data.find(c => c.status === 'ACTIVE' && c.erpType !== 'DEMO');
      if (active) {
        updateConnectionStatus(ERP_LABELS[active.erpType] ?? 'Connected', 'connected');
        companySwitcher.setErp(ERP_LABELS[active.erpType] ?? null);
      } else {
        updateConnectionStatus('Not connected', 'disconnected');
        companySwitcher.setErp('No ERP');
      }
    } catch (error) {
      console.error('ERP status check failed:', error);
      updateConnectionStatus('Unknown', 'unknown');
    }
  }

  async function updateLicenseStatus(isDemoTemplate = false) {
    const statusEl = document.getElementById('license-status');
    if (!statusEl) return;

    // In a shared demo session no subscription applies (formula execution is
    // bypassed for demo templates), so "Expired" would be misleading — show
    // the demo state instead.
    if (isDemoTemplate) {
      statusEl.textContent = 'Demo';
      statusEl.className = 'status-active';
      return;
    }

    const license = await authService.checkLicense();
    if (license.state === 'unknown') {
      statusEl.textContent = 'Could not check';
      statusEl.className = '';
      return;
    }
    statusEl.textContent = LICENSE_LABELS[license.state];
    statusEl.className = license.state === 'active' || license.state === 'trial' ? 'status-active' : 'status-expired';
  }

  /**
   * Report the outcome of a dashboard action where the user can see it. These
   * used to go only to the screen-reader live region, so a sighted user pressed
   * a destructive button and got no confirmation that anything had happened.
   * The element is `role="status" aria-live="polite"`, so it still announces.
   */
  function showActionResult(message: string, tone: 'success' | 'error' = 'success'): void {
    const el = document.getElementById('action-result');
    if (!el) {
      // No dashboard in the DOM (e.g. a ribbon press with the pane closed).
      if (tone === 'error') accessibilityManager.announceError(message);
      else accessibilityManager.announceSuccess(message);
      return;
    }
    el.textContent = message;
    el.className = `action-result action-result--${tone}`;
    window.setTimeout(() => {
      if (el.textContent === message) {
        el.textContent = '';
        el.className = 'action-result';
      }
    }, 12000);
  }

  /**
   * Same slot as showActionResult, but with a real anchor. Used where the host
   * can't open a browser for us and the user has to supply the click.
   */
  function showActionLink(message: string, url: string, linkText: string): void {
    const el = document.getElementById('action-result');
    if (!el) return;
    el.className = 'action-result';
    el.innerHTML =
      `${escapeHtml(message)} <a href="${escapeHtml(url)}" target="_blank" rel="noopener">${escapeHtml(linkText)}</a>`;
  }

  /** The remove link is only worth showing while the sheet is actually there. */
  async function refreshClaudeSheetLink(): Promise<void> {
    const link = document.getElementById('action-remove-claude-sheet');
    if (!link) return;
    try {
      const { VOCAB_SHEET_NAME } = await import('../services/ClaudeVocabularyRows');
      const exists = await Excel.run(async (context) => {
        const sheets = context.workbook.worksheets;
        sheets.load('items/name');
        await context.sync();
        return sheets.items.some(sheet => sheet.name === VOCAB_SHEET_NAME);
      });
      link.style.display = exists ? 'block' : 'none';
    } catch {
      link.style.display = 'none';
    }
  }

  /**
   * The open inline panel's restore function, if one is showing. A dialog is a
   * question about the workbook as it is right now, so anything that changes
   * the workbook — or navigates away — has to take it down rather than leave a
   * stale cell count on screen waiting to be confirmed.
   */
  let openPanelRestore: (() => void) | null = null;

  function dismissActionPanel(): void {
    openPanelRestore?.();
  }

  /**
   * Show an inline panel in place of the action buttons and hand back a restore
   * function. The pane is too narrow for a real modal and Office dialogs run in
   * their own runtime, so confirmations are drawn in place — but in a container
   * of their own rather than by overwriting the buttons, which would drop every
   * listener and reset the force-refresh checkbox on each cancel.
   */
  function openActionPanel(html: string): (() => void) | null {
    const groups = document.getElementById('action-groups');
    const dialog = document.getElementById('action-dialog');
    if (!groups || !dialog) return null;

    // Only one panel at a time, and never one left behind a later action.
    dismissActionPanel();

    groups.style.display = 'none';
    dialog.innerHTML = html;
    dialog.style.display = 'block';

    const restore = () => {
      dialog.innerHTML = '';
      dialog.style.display = 'none';
      groups.style.display = 'block';
      if (openPanelRestore === restore) openPanelRestore = null;
    };
    openPanelRestore = restore;
    return restore;
  }

  /**
   * Hardcoding is destructive and effectively one-way, so the scope is chosen
   * here rather than by which button you happened to press, and the count of
   * affected cells is shown before anything is written. Workbook is the default
   * because the reason people ask for this is sending the file outside the
   * company.
   */
  function showHardcodeDialog(presetScope: HardcodeScope = 'workbook'): void {
    const scopeChecked = (scope: HardcodeScope) => (scope === presetScope ? ' checked' : '');
    const restore = openActionPanel(`
      <div class="confirm-inline" role="alertdialog" aria-labelledby="hardcode-title">
        <p id="hardcode-title" class="confirm-message">
          Replace ${escapeHtml(ADDIN_NAMESPACE)} formulas with the values they show now.
          Everything else is left as it is.
        </p>
        <fieldset class="hardcode-scope">
          <legend class="hardcode-legend">Apply to</legend>
          <label><input type="radio" name="hardcode-scope" value="workbook"${scopeChecked('workbook')} /> Whole workbook</label>
          <label><input type="radio" name="hardcode-scope" value="sheet"${scopeChecked('sheet')} /> Active sheet</label>
          <label><input type="radio" name="hardcode-scope" value="selection"${scopeChecked('selection')} /> Current selection</label>
        </fieldset>
        <label class="hardcode-all">
          <input type="checkbox" id="hardcode-refresh-first" checked />
          <span>Fetch fresh values from the ERP first — slower, but the frozen
                numbers won't be up to 5 minutes old</span>
        </label>
        <label class="hardcode-all">
          <input type="checkbox" id="hardcode-all-formulas" />
          <span>Convert every formula, not just ${escapeHtml(ADDIN_NAMESPACE)} ones</span>
        </label>
        <p id="hardcode-preview" class="hardcode-preview">Counting…</p>
        <div class="confirm-actions">
          <button class="ms-Button" id="confirm-cancel">
            <span class="ms-Button-label">Cancel</span>
          </button>
          <button class="ms-Button ms-Button--primary" id="confirm-ok" disabled>
            <span class="ms-Button-label">Hardcode</span>
          </button>
        </div>
      </div>
    `);
    if (!restore) return;

    const previewEl = document.getElementById('hardcode-preview');
    const okBtn = document.getElementById('confirm-ok') as HTMLButtonElement | null;
    const cancelBtn = document.getElementById('confirm-cancel');
    const allFormulasBox = document.getElementById('hardcode-all-formulas') as HTMLInputElement | null;
    const refreshFirstBox = document.getElementById('hardcode-refresh-first') as HTMLInputElement | null;

    const currentScope = (): HardcodeScope => {
      const checked = document.querySelector<HTMLInputElement>('input[name="hardcode-scope"]:checked');
      return (checked?.value as HardcodeScope) || 'workbook';
    };
    const currentOptions = () => ({ allFormulas: allFormulasBox?.checked === true });

    let previewToken = 0;
    const updatePreview = async () => {
      const token = ++previewToken;
      if (previewEl) previewEl.textContent = 'Counting…';
      if (okBtn) okBtn.disabled = true;
      try {
        const { replaced, skipped, noFigure } = await hardcodeService.preview(currentScope(), currentOptions());
        if (token !== previewToken) return;
        const owed = owedConversionRefusal(refreshFirstBox?.checked === true);
        if (previewEl) {
          previewEl.textContent = owed ?? (noFigure > 0
            ? noFigurePreview(noFigure)
            : replaced === 0
              ? 'Nothing to convert here.'
              : `${replaced} formula(s) become values. ${skipped} other formula(s) stay live.`);
        }
        // "Fetch fresh values first" may give the waiting cells their figures.
        if (okBtn) okBtn.disabled = replaced === 0 && noFigure === 0;
      } catch (error) {
        if (token !== previewToken) return;
        console.error('Hardcode preview error:', error);
        // A selection scope on a multi-area selection is the usual cause. Let
        // the user proceed — apply reports the real error if it still fails.
        if (previewEl) previewEl.textContent = 'Could not count the cells in this scope.';
        if (okBtn) okBtn.disabled = false;
      }
    };

    document.querySelectorAll<HTMLInputElement>('input[name="hardcode-scope"]').forEach(radio => {
      radio.addEventListener('change', () => void updatePreview());
    });
    allFormulasBox?.addEventListener('change', () => void updatePreview());
    refreshFirstBox?.addEventListener('change', () => void updatePreview());

    cancelBtn?.addEventListener('click', restore);

    // One pane action from the refresh to the conversion, so no recalculation the pane did not start
    // turns cells back into #BUSY! before they are read.
    okBtn?.addEventListener('click', () => duringPaneAction(async () => {
      const scope = currentScope();
      const options = currentOptions();
      const refreshFirst = refreshFirstBox?.checked === true;
      restore();
      try {
        const manualRefusal = await manualConversionRefusal(options.allFormulas, refreshFirst);
        if (manualRefusal) {
          showActionResult(manualRefusal, 'error');
          return;
        }
        if (refreshFirst) {
          // batchRecalc has no selection scope; refreshing the sheet the
          // selection lives on is the closest honest equivalent.
          const refreshed = await batchRecalc(scope === 'workbook' ? 'workbook' : 'sheet', { forceRefresh: true });
          if (!refreshed) return;
        }
        // Frozen figures from another company, or its error text, would stay for good, and so would
        // figures a move or a binding has not recalculated yet. Checked after the refresh too: another
        // workbook's pane can switch the company while it runs.
        const refusal = (await workbookPanel.refusal('converting formulas to values')) ?? owedConversionRefusal(refreshFirst);
        if (refusal) {
          showActionResult(refusal, 'error');
          return;
        }
        const { replaced, skipped, noFigure } = await hardcodeService.hardcode(scope, options);
        if (noFigure > 0) {
          showActionResult(noFigureMessage(noFigure), 'error');
          return;
        }
        showActionResult(
          `Converted ${replaced} formula(s) to values${refreshFirst ? ' after refreshing from the ERP' : ''}. ` +
          `${skipped} other formula(s) left untouched.`
        );
      } catch (error) {
        console.error('Hardcode error:', error);
        showActionResult(
          error instanceof Error ? error.message : 'Could not hardcode this scope',
          'error'
        );
      }
    }));

    cancelBtn?.focus();
    void updatePreview();
  }

  // Click the nav tab rather than calling showView directly, so the tab
  // highlight and any lazy load happen the way they always do.
  const goToView = (view: string) => {
    if (view !== 'dashboard') dismissActionPanel();
    document.querySelector<HTMLElement>(`.nav-btn[data-view="${view}"]`)?.click();
  };

  // Hand the ribbon the same implementations the pane uses.
  ribbonActions = {
    // The progress bar and the result line both live in the dashboard view, so
    // a recalc triggered from any other tab would run correctly and report
    // into a hidden container.
    recalc: async (scope, options) => {
      goToView('dashboard');
      await batchRecalc(scope, options);
    },
    hardcode: (scope) => {
      // The dialog renders inside the dashboard view. Triggered from the ribbon
      // while another tab is open it would otherwise appear in a hidden
      // container, and the ribbon would look like it had done nothing.
      goToView('dashboard');
      showHardcodeDialog(scope);
    },
    goTo: (view) => goToView(view),
    openConnection: () => {
      goToView('dashboard');
      // The view is shown on the next animation frame; scroll once it is.
      setTimeout(() => document.getElementById('connection-section')?.scrollIntoView(), 150);
    },
    clearCache: async () => {
      goToView('dashboard');
      try {
        await dropCachedResults();
        showActionResult('Cleared the local formula cache. The next calculation goes back to the server.');
      } catch (error) {
        console.error('Clear cache error:', error);
        showActionResult('Could not clear the cache', 'error');
      }
    },
    prepareForClaude: () => {
      goToView('dashboard');
      document.getElementById('action-prepare-claude')?.click();
    },
    openExternal: async (url, label) => {
      // openBrowserWindow is the supported way out of the host, but
      // OpenBrowserWindowApi is explicitly NOT supported on Office on the web.
      if (Office.context.ui?.openBrowserWindow) {
        Office.context.ui.openBrowserWindow(url);
        return;
      }

      // On the web the pane is a cross-origin iframe and the click came from
      // the ribbon, so there is no user gesture here and the popup is blocked
      // without a word. Offer a link instead: clicking it IS a gesture.
      const opened = window.open(url, '_blank', 'noopener');
      if (opened) return;

      await showTaskpane();
      goToView('dashboard');
      showActionLink(`Your browser blocked the new tab.`, url, `Open ${label}`);
    },
  };

  startProgressMonitoring();
}

interface ProgressData {
  loading: boolean;
  progress: number;
  status: string;
  message?: string;
  completedFormulas?: number;
  totalFormulas?: number;
  error?: string;
}

interface ActiveBatchInfo {
  batchId: string;
  timestamp: number;
}

let progressPollInterval: ReturnType<typeof setInterval> | null = null;
/** While the pane's Recalc runs it owns the progress box; the batch poller stays out of it. */
let paneRecalcRunning = false;
let currentBatchId: string | null = null;
let taskpaneApiService: ApiService | null = null;
let taskpaneTokenManager: TokenManager | null = null;
let taskpaneDeviceFingerprint: DeviceFingerprint | null = null;

function getTaskpaneApiService(): ApiService {
  if (!taskpaneApiService) {
    taskpaneApiService = new ApiService();
  }
  return taskpaneApiService;
}

function getTaskpaneTokenManager(): TokenManager {
  if (!taskpaneTokenManager) {
    taskpaneTokenManager = new TokenManager();
  }
  return taskpaneTokenManager;
}

function getTaskpaneDeviceFingerprint(): DeviceFingerprint {
  if (!taskpaneDeviceFingerprint) {
    taskpaneDeviceFingerprint = new DeviceFingerprint();
  }
  return taskpaneDeviceFingerprint;
}

function startProgressMonitoring(): void {
  if (progressPollInterval) return;

  progressPollInterval = setInterval(() => {
    checkForActiveBatch();
  }, 1000);
}

async function checkForActiveBatch(): Promise<void> {
  if (paneRecalcRunning) return;
  try {
    if (typeof OfficeRuntime !== 'undefined' && OfficeRuntime.storage) {
      const batchData = await OfficeRuntime.storage.getItem('bifrost_active_batch');

      if (!batchData) {
        if (currentBatchId) {
          currentBatchId = null;
          hideProgressUI();
        }
        return;
      }

      const batchInfo: ActiveBatchInfo = JSON.parse(batchData);

      if (Date.now() - batchInfo.timestamp > 300000) {
        await OfficeRuntime.storage.removeItem('bifrost_active_batch');
        currentBatchId = null;
        hideProgressUI();
        return;
      }

      if (batchInfo.batchId !== currentBatchId) {
        currentBatchId = batchInfo.batchId;
        hideProgressUI();
      }

      await pollBackendProgress(batchInfo.batchId);
    }
  } catch (err) {
    console.warn('Progress check error:', err);
  }
}

async function pollBackendProgress(batchId: string): Promise<void> {
  try {
    const tokenManager = getTaskpaneTokenManager();
    const token = await tokenManager.getToken();

    if (!token) return;

    const deviceFingerprint = getTaskpaneDeviceFingerprint();
    const fingerprint = await deviceFingerprint.generate();
    const apiService = getTaskpaneApiService();

    const response = await apiService.get<{
      loading: boolean;
      progress: number;
      status: string;
      message?: string;
      completedFormulas?: number;
      totalFormulas?: number;
      error?: string;
    }>(`/formulas/batch/${batchId}/progress`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'x-device-fingerprint': fingerprint
      }
    });

    if (!response.success) {
      console.warn(`Progress poll failed: ${response.error}`);
      return;
    }

    const data = response.data;
    if (!data) return;

    if (data.loading) {
      showProgressUI({
        loading: data.loading,
        progress: data.progress || 0,
        status: data.status || 'fetching',
        message: data.message,
        completedFormulas: data.completedFormulas,
        totalFormulas: data.totalFormulas
      });
    } else {
      if (data.error) {
        showProgressError(data.error);
      } else {
        showProgressComplete();
      }
      currentBatchId = null;
    }
  } catch (err) {
    console.warn('Backend progress poll error:', err);
  }
}

function showProgressUI(progress: ProgressData): void {
  const container = document.getElementById('loading-progress');
  const title = document.getElementById('loading-progress-title');
  const fill = document.getElementById('progress-fill');
  const progressBar = container?.querySelector('.progress-bar');
  const status = document.getElementById('loading-progress-status');
  const percent = document.getElementById('loading-progress-percent');

  if (!container) return;

  container.style.display = 'block';

  if (title) {
    const statusMessages: Record<string, string> = {
      'pending': 'Initializing...',
      'fetching': 'Fetching data from ERP...',
      'calculating': 'Calculating formulas...',
      'complete': 'Complete!'
    };
    title.textContent = statusMessages[progress.status] || progress.message || 'Loading...';
  }

  if (fill) {
    fill.style.width = `${progress.progress}%`;
  }
  if (progressBar) {
    progressBar.setAttribute('aria-valuenow', String(progress.progress));
  }

  if (status) {
    if (progress.completedFormulas !== undefined && progress.totalFormulas) {
      status.textContent = `${progress.completedFormulas} of ${progress.totalFormulas} formulas`;
    } else {
      status.textContent = progress.message || 'Processing...';
    }
  }

  if (percent) {
    percent.textContent = `${progress.progress}%`;
  }
}

function showProgressComplete(): void {
  const container = document.getElementById('loading-progress');
  const title = document.getElementById('loading-progress-title');
  const fill = document.getElementById('progress-fill');
  const progressBar = container?.querySelector('.progress-bar');
  const status = document.getElementById('loading-progress-status');
  const percent = document.getElementById('loading-progress-percent');

  if (!container) return;

  container.style.display = 'block';

  if (title) title.textContent = 'Data loaded successfully!';
  if (fill) fill.style.width = '100%';
  if (progressBar) progressBar.setAttribute('aria-valuenow', '100');
  if (status) status.textContent = 'Complete';
  if (percent) percent.textContent = '100%';

  setTimeout(() => {
    hideProgressUI();
  }, 2000);
}

function showProgressError(error: string): void {
  const container = document.getElementById('loading-progress');
  const title = document.getElementById('loading-progress-title');
  const status = document.getElementById('loading-progress-status');

  if (!container) return;

  container.style.display = 'block';
  container.style.borderColor = '#a4262c';
  container.style.background = 'linear-gradient(135deg, #fde7e9 0%, #fff5f5 100%)';

  if (title) {
    title.textContent = 'Error loading data';
    title.style.color = '#a4262c';
  }
  if (status) {
    status.textContent = error;
    status.style.color = '#a4262c';
  }

  setTimeout(() => {
    hideProgressUI();
    if (container) {
      container.style.borderColor = '';
      container.style.background = '';
    }
    if (title) title.style.color = '';
    if (status) status.style.color = '';
  }, 5000);
}

function hideProgressUI(): void {
  const container = document.getElementById('loading-progress');
  if (container) {
    container.style.display = 'none';
  }
}

/**
 * Full-pane blocking overlay shown during company switches. Prevents the
 * race where any company-dependent action fired between /switch returning
 * a new JWT and the UI re-rendering would use the OLD context.
 *
 * Inline styles deliberately — keeps the overlay self-contained and
 * removes the chance of CSS file-load timing affecting visibility.
 */
function showSwitchingOverlay(companyName: string): void {
  hideSwitchingOverlay(); // idempotent — clean any previous overlay first

  const overlay = document.createElement('div');
  overlay.id = 'company-switch-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: 'rgba(255, 255, 255, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '99999',
    cursor: 'wait',
    backdropFilter: 'blur(2px)',
  });

  const content = document.createElement('div');
  Object.assign(content.style, {
    background: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    padding: '20px 28px',
    textAlign: 'center',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
    minWidth: '220px',
    maxWidth: '320px',
  });

  const spinner = document.createElement('div');
  Object.assign(spinner.style, {
    width: '28px',
    height: '28px',
    border: '3px solid #e5e7eb',
    borderTopColor: '#2563eb',
    borderRadius: '50%',
    margin: '0 auto 12px',
    animation: 'bifrost-spin 0.8s linear infinite',
  });

  const message = document.createElement('div');
  message.style.fontSize = '14px';
  message.style.color = '#1f2937';
  message.textContent = `Switching to ${companyName}…`;

  // Animation keyframes (inject once)
  if (!document.getElementById('bifrost-overlay-style')) {
    const style = document.createElement('style');
    style.id = 'bifrost-overlay-style';
    style.textContent = '@keyframes bifrost-spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
  }

  content.appendChild(spinner);
  content.appendChild(message);
  overlay.appendChild(content);
  document.body.appendChild(overlay);
}

function hideSwitchingOverlay(): void {
  // removeChild, not remove(): the desktop ES5 fallback has no Element.remove.
  const overlay = document.getElementById('company-switch-overlay');
  overlay?.parentNode?.removeChild(overlay);
}
